import { currentYear } from "@/lib/siteYear";
// SEO/AIO landing config for /air-compressors/category/:slug.
// Article + affiliate CTA pattern (no dedicated product table yet).

export type AirCompressorGroup =
  | "type"
  | "use"
  | "tank"
  | "power"
  | "noise"
  | "spec"
  | "brand";

export interface AirCompressorSubcategoryConfig {
  slug: string;
  title: string;
  shortLabel: string;
  group: AirCompressorGroup;
  h1: string;
  metaTitle: string;
  metaDescription: string;
  intro: string;
  longCopy: string[];
  faqs: { q: string; a: string }[];
  amazonQuery: string; // search keywords for the affiliate CTA
  /** Lower-cased tag/keyword variants used for matching articles. */
  tagKeywords: string[];
}

const Y = currentYear();

type Seed = Omit<AirCompressorSubcategoryConfig, "tagKeywords"> & {
  tagKeywords?: string[];
};

const seeds: Seed[] = [
  // ============ BY TYPE ============
  {
    slug: "silent-air-compressors",
    title: "Silent Air Compressors",
    shortLabel: "Silent",
    group: "type",
    h1: `Best Silent Air Compressors UK ${Y}`,
    metaTitle: `Best Silent Air Compressors UK ${Y} | Whisper-Quiet Reviews`,
    metaDescription:
      "Independent UK reviews of the best silent air compressors. Compare ultra-quiet oil-free models from 6L to 50L for home workshops, garages and indoor use.",
    intro:
      "Silent air compressors deliver the airflow you need without the migraine-inducing racket of a standard piston compressor. Modern oil-free silent models run at 55–65 dB — quieter than a normal conversation — making them ideal for indoor garages, workshops and shared spaces.",
    longCopy: [
      "Most ‘silent’ compressors achieve their low noise rating by combining brushless or low-RPM induction motors with sound-dampened oil-free pump heads. Expect 55–70 dB depending on tank size — roughly half the perceived loudness of a typical 90 dB DIY compressor.",
      "Trade-offs: silent models usually have smaller CFM output than belt-drive piston rivals, so they're best for inflation, brad nailing, airbrushing and short bursts of impact work rather than continuous spray painting or sanding.",
      "Top picks in the UK include the Bambi range, Hyundai HY27552, SGS silent compressors and Clarke Bandit. Budget for £150–£400 depending on tank size and duty cycle.",
    ],
    faqs: [
      { q: "How quiet is a silent air compressor?", a: "Genuine silent models run at 55–65 dB at one metre, similar to a domestic dishwasher. Standard direct-drive compressors are typically 85–95 dB." },
      { q: "Are silent compressors powerful enough for spray painting?", a: "Most consumer silent compressors deliver 1.5–2.5 CFM — fine for touch-ups and airbrushing but underpowered for sustained car panel spraying, which needs 7+ CFM." },
      { q: "Do silent air compressors need oil?", a: "The vast majority are oil-free, which is part of how they stay quiet and low-maintenance. Always check the spec — a few belt-driven ‘low noise’ models still use oil." },
    ],
    amazonQuery: "silent air compressor",
  },
  {
    slug: "oil-free-air-compressors",
    title: "Oil-Free Air Compressors",
    shortLabel: "Oil-Free",
    group: "type",
    h1: `Best Oil-Free Air Compressors UK ${Y}`,
    metaTitle: `Best Oil-Free Air Compressors UK ${Y} | Reviews & Buying Guide`,
    metaDescription:
      "The best oil-free air compressors in the UK reviewed. Compare low-maintenance, clean-air models from DeWalt, Makita, Hyundai, Clarke and SIP.",
    intro:
      "Oil-free air compressors skip the messy oil reservoir and Teflon-lined cylinders, giving you cleaner air and zero oil-change maintenance. They're the go-to choice for spray painting, dental and food-safe applications, and any user who'd rather not service a pump.",
    longCopy: [
      "Oil-free pumps use self-lubricating polymer rings instead of an oil bath. That means no oil mist in the output air, no annual oil change, and instant cold-weather starts — but typically a shorter pump lifespan than equivalent oil-lubricated compressors.",
      "Look for direct-drive oil-free models for portability (6–24L tanks) or belt-drive oil-free for higher duty cycles in workshops. Expect 1.5–6 CFM depending on motor size.",
      "Strong UK options include the DeWalt DPC6MRC, Makita MAC2400, Hyundai HY24-100, Clarke Bandit and SIP Airmate ranges.",
    ],
    faqs: [
      { q: "Are oil-free air compressors any good?", a: "Yes — for intermittent DIY and trade use they offer cleaner output air, instant starts and zero maintenance. For 8-hour-a-day commercial duty, oil-lubricated belt-drive compressors still last longer." },
      { q: "How long do oil-free air compressors last?", a: "Quality oil-free pumps deliver 500–2000 hours of use; oil-lubricated equivalents typically last 3000–5000 hours under the same load." },
      { q: "Can I spray paint with an oil-free compressor?", a: "Yes — oil-free models actually deliver cleaner air for spraying than poorly-maintained oil compressors, as long as you size the CFM correctly for your spray gun." },
    ],
    amazonQuery: "oil free air compressor",
  },
  {
    slug: "belt-drive-air-compressors",
    title: "Belt Drive Air Compressors",
    shortLabel: "Belt Drive",
    group: "type",
    h1: `Best Belt Drive Air Compressors UK ${Y}`,
    metaTitle: `Best Belt Drive Air Compressors UK ${Y} | Workshop Compressor Reviews`,
    metaDescription:
      "Independent UK reviews of belt drive air compressors. Compare professional workshop and garage compressors with higher CFM and longer duty cycles.",
    intro:
      "Belt drive compressors run the motor at a lower RPM than direct-drive units, which means cooler operation, lower noise and dramatically longer pump life. They're the workhorse of choice for serious workshops, garages and trade users.",
    longCopy: [
      "A belt drive layout decouples motor and pump speed, allowing the pump to run at 600–1200 RPM versus 2800+ RPM on direct-drive models. The result is lower wear, better airflow per kW and an easy belt replacement when service is due.",
      "Most belt-drive compressors are floor-standing units with 50L–270L vertical or horizontal tanks, oil-lubricated cast-iron pumps and 2HP–5HP motors. Expect 7–25 CFM depending on size.",
      "UK favourites include the SIP Airmate ISBD, Hyundai HY3050V, Clarke SE16C150 and Sealey SAC2503B. Three-phase models are available for industrial users.",
    ],
    faqs: [
      { q: "Belt drive vs direct drive air compressor — which is better?", a: "Belt drive wins for sustained workshop use thanks to longer pump life and lower noise. Direct drive wins for portability, lower cost and intermittent DIY use." },
      { q: "How long does a belt drive compressor last?", a: "With basic oil and belt servicing, a good belt-drive workshop compressor will run 8–15 years of trade use — often 20+ years for hobbyists." },
      { q: "Do belt drive compressors need oil?", a: "Almost all belt drive compressors are oil-lubricated, with a sight glass and drain on the pump. Plan on an oil change every 200–500 running hours." },
    ],
    amazonQuery: "belt drive air compressor",
  },
  {
    slug: "direct-drive-air-compressors",
    title: "Direct Drive Air Compressors",
    shortLabel: "Direct Drive",
    group: "type",
    h1: `Best Direct Drive Air Compressors UK ${Y}`,
    metaTitle: `Best Direct Drive Air Compressors UK ${Y} | Compact Compressor Reviews`,
    metaDescription:
      "The best direct drive air compressors in the UK — compact, portable and ideal for DIY, nailing, tyre inflation and intermittent workshop use.",
    intro:
      "Direct drive air compressors couple the motor straight to the pump for a compact, lightweight, lower-cost design. They're perfect for portable use, second-floor workshops and any DIY job that doesn't demand a continuous duty cycle.",
    longCopy: [
      "Direct drive layouts strip out the belts, pulleys and guards of a belt-drive compressor, giving you a smaller and cheaper unit at the same tank size. Most direct-drive models are oil-free, making them maintenance-light too.",
      "Expect 1.5–6 CFM, tank sizes from 6L up to 50L, and noise levels of 70–95 dB. They're best for short-burst applications: nail guns, staplers, tyre inflation, brad nailers and airbrushing.",
      "Top UK direct-drive picks include the DeWalt DPC6MRC, Makita MAC2400, Bostitch BTFP02012, Hyundai HY7524-50 and SIP Airmate Tornado.",
    ],
    faqs: [
      { q: "What is a direct drive air compressor?", a: "A compressor where the electric motor connects straight to the pump crankshaft, with no intermediate belt and pulley." },
      { q: "Are direct drive compressors loud?", a: "Standard direct-drive models run 85–95 dB. Silent direct-drive variants now reach 55–65 dB but at higher cost." },
      { q: "Can a direct drive compressor run all day?", a: "Most are rated for ~50% duty cycle — run for 30 minutes, rest 30. For 8-hour continuous use, choose a belt-drive workshop compressor." },
    ],
    amazonQuery: "direct drive air compressor",
  },
  {
    slug: "screw-air-compressors",
    title: "Screw Compressors",
    shortLabel: "Screw",
    group: "type",
    h1: `Best Screw Air Compressors UK ${Y}`,
    metaTitle: `Best Screw Air Compressors UK ${Y} | Rotary Screw Compressor Guide`,
    metaDescription:
      "Independent UK guide to rotary screw air compressors. Compare 3kW–22kW screw compressors for industrial workshops, body shops and continuous-duty applications.",
    intro:
      "Rotary screw compressors deliver continuous, smooth, high-volume air at a quieter operating noise than piston compressors. They're the standard choice for body shops, factories and any workshop that runs tools for hours at a time.",
    longCopy: [
      "Unlike piston compressors that pulse air with each stroke, screw compressors use two intermeshing helical rotors to deliver a constant, vibration-free airflow. That makes them ideal for spray booths, sandblasters, plasma cutters and large impact tools.",
      "Entry-level 3kW screw compressors deliver around 10 CFM at 8 bar and can run 100% duty cycle. Step up to 7.5–11kW for body shop work, and 15–22kW for full industrial use. Variable-speed (VSD) models cut electricity bills by 30–50% on partial loads.",
      "UK suppliers and brands worth shortlisting include ABAC, Atlas Copco, Hyundai, Ingersoll Rand, SIP and FIAC. Expect £1,800–£8,000+ for a quality fixed-speed screw compressor.",
    ],
    faqs: [
      { q: "Is a screw compressor better than a piston compressor?", a: "For continuous duty (over 50% of an 8-hour day) yes — screws are quieter, longer-lasting and far more efficient. For intermittent use a piston compressor is cheaper and perfectly fine." },
      { q: "How long does a screw compressor last?", a: "Quality rotary screw compressors typically last 40,000–60,000 hours with regular oil, separator and air-end servicing — often 15–20 years of trade use." },
      { q: "Do I need three-phase power for a screw compressor?", a: "Most screws above 3kW require 415V three-phase. Single-phase 240V screw compressors do exist up to ~5.5kW but are more expensive." },
    ],
    amazonQuery: "rotary screw air compressor",
  },
  {
    slug: "piston-air-compressors",
    title: "Piston Air Compressors",
    shortLabel: "Piston",
    group: "type",
    h1: `Best Piston Air Compressors UK ${Y}`,
    metaTitle: `Best Piston Air Compressors UK ${Y} | Reciprocating Compressor Reviews`,
    metaDescription:
      "The best piston (reciprocating) air compressors in the UK reviewed. Compare oil and oil-free single and twin-cylinder workshop compressors.",
    intro:
      "Piston air compressors are the most popular type in UK workshops thanks to their simplicity, low cost and easy serviceability. From a 6L pancake compressor to a 200L twin-cylinder workshop unit, the reciprocating piston layout still dominates the under-£1500 market.",
    longCopy: [
      "A piston compressor pulls air into a cylinder, compresses it on the upstroke, and pushes it into the storage tank — exactly like a car engine running in reverse. Single-stage piston compressors top out around 10 bar, while two-stage piston compressors comfortably hit 12–15 bar for tougher tasks.",
      "Choose single-cylinder for DIY use up to 6 CFM, twin-cylinder for serious workshop work up to 15 CFM, and two-stage for sandblasting, spraying and heavier impact tools.",
      "Major UK piston compressor brands include Hyundai, SIP, Clarke, Sealey, ABAC, DeWalt and Bostitch.",
    ],
    faqs: [
      { q: "What is a piston compressor used for?", a: "Tyre inflation, nail guns, staplers, impact wrenches, spray painting, sandblasting and general workshop air tools — essentially every common DIY and trade application." },
      { q: "Single-stage or two-stage piston compressor?", a: "Single-stage is fine up to about 10 bar for tools needing under 10 CFM. Two-stage is better for sustained work above 10 bar and for sandblasting or high-volume spray jobs." },
      { q: "How long does a piston compressor last?", a: "A quality oil-lubricated belt-drive piston compressor lasts 3,000–5,000 hours of trade use. Oil-free direct-drive piston models last 500–2,000 hours." },
    ],
    amazonQuery: "piston air compressor",
  },
  {
    slug: "portable-air-compressors",
    title: "Portable Air Compressors",
    shortLabel: "Portable",
    group: "type",
    h1: `Best Portable Air Compressors UK ${Y}`,
    metaTitle: `Best Portable Air Compressors UK ${Y} | Compact & Mobile Compressors`,
    metaDescription:
      "Reviews of the best portable air compressors for UK DIY and mobile trade use. Compare lightweight pancake, hotdog and wheeled models from 6L to 50L.",
    intro:
      "Portable air compressors put workshop-grade air on a building site, in the back of a van or in a tight loft conversion. From sub-12 kg pancake compressors to wheeled 50L barrel units, this is the most versatile category for trade and serious DIY users.",
    longCopy: [
      "‘Portable’ covers everything from a 6L hand-carry compressor for first-fix nailing up to a 50L wheelbarrow-style unit for site work. Match weight, CFM and tank size to the longest single job you'll do without rest.",
      "For first-fix carpentry and trim work, a 9.5L 2HP oil-free pancake compressor delivering 2.5 CFM is perfect. For mobile tyre fitting, choose a 24L wheeled compressor with 6+ CFM. For roofing, a twin-tank ‘hotdog’ gives you longer run-time between cycles.",
      "Strong UK portable picks include the DeWalt DPC6MRC, Bostitch BTFP02012, Makita MAC2400 and the Hyundai HY7524 wheeled range.",
    ],
    faqs: [
      { q: "What size portable air compressor do I need?", a: "For nailing and brad work, 6L is fine. For tyre inflation and impact wrenches, 24L is the sweet spot. For sustained spray work on the move, you'll want 50L." },
      { q: "Can portable compressors run impact wrenches?", a: "Yes — a 24L portable delivering 4+ CFM at 90 PSI will run most ½″ impact wrenches for tyre work. Sustained high-torque jobs need a larger workshop compressor." },
      { q: "Are portable compressors loud?", a: "Standard portable compressors run 85–95 dB. Choose a silent portable model (55–65 dB) for indoor or noise-sensitive jobs." },
    ],
    amazonQuery: "portable air compressor",
  },
  {
    slug: "vertical-air-compressors",
    title: "Vertical Air Compressors",
    shortLabel: "Vertical",
    group: "type",
    h1: `Best Vertical Air Compressors UK ${Y}`,
    metaTitle: `Best Vertical Air Compressors UK ${Y} | Upright Workshop Compressors`,
    metaDescription:
      "Independent UK reviews of vertical (upright) air compressors. Compare 50L–270L space-saving workshop compressors for garages and trade workshops.",
    intro:
      "Vertical air compressors stack their tank upright, which dramatically reduces floor footprint — perfect for narrow garages, body shops and workshops where wall space is at a premium. Most large 100L+ compressors in the UK are now vertical.",
    longCopy: [
      "A vertical tank takes the same volume of stored air as a horizontal one but occupies roughly a third of the floor area. The trade-off is height — make sure your workshop has at least 1.7–2.0 m clearance for a 200L+ vertical compressor.",
      "Vertical compressors are typically belt-drive with cast-iron pumps and 2HP–5HP motors, delivering 8–20 CFM. Both single-phase 240V and 3-phase 415V versions are common.",
      "UK favourites include the Hyundai HY3150V, SIP Airmate ISBD11/200, Clarke SE16C200V and Sealey SAC52775B.",
    ],
    faqs: [
      { q: "Vertical vs horizontal air compressor?", a: "Choose vertical to save floor space in narrow workshops. Choose horizontal if you have low ceilings or want a more stable footprint for occasional moves." },
      { q: "Are vertical compressors top-heavy?", a: "Quality vertical compressors have wide base plates and are designed to bolt to a concrete floor for safety. Always anchor a 150L+ vertical unit." },
      { q: "What CFM do I need for body shop work?", a: "Plan on 12–18 CFM at 8 bar for sustained body shop spraying and sanding. A 150–200L vertical compressor sized for that load is ideal." },
    ],
    amazonQuery: "vertical air compressor",
  },
  {
    slug: "horizontal-air-compressors",
    title: "Horizontal Air Compressors",
    shortLabel: "Horizontal",
    group: "type",
    h1: `Best Horizontal Air Compressors UK ${Y}`,
    metaTitle: `Best Horizontal Air Compressors UK ${Y} | Floor-Standing Compressor Reviews`,
    metaDescription:
      "The best horizontal air compressors in the UK reviewed. Compare 50L–270L floor-standing workshop compressors with low-profile stability.",
    intro:
      "Horizontal air compressors lay the tank flat for low-profile, super-stable installation — ideal for low-ceiling garages, mobile workshops and any setup that prefers a wider footprint to height.",
    longCopy: [
      "Horizontal compressors typically share the same pump, motor and CFM specs as their vertical equivalents — only the tank orientation differs. Most are belt-drive with cast-iron oil-lubricated pumps.",
      "They're popular for mobile workshop trailers and any garage with limited headroom. A wheeled horizontal 100L compressor can usually fit through a standard 760mm door.",
      "Strong UK options include the SIP Airmate 100/E, Hyundai HY30100, Clarke SE25C150 and Sealey SAC1153B.",
    ],
    faqs: [
      { q: "Is a horizontal compressor more stable?", a: "Yes — lower centre of gravity makes horizontal compressors less prone to tipping when moved on castors. Vertical compressors are best bolted down." },
      { q: "Can a horizontal compressor fit through a normal doorway?", a: "Most 100L horizontal compressors are around 70 cm tall — easily through a 76 cm door. Tank length is the bigger constraint, often 110–130 cm." },
      { q: "Do horizontal and vertical compressors perform the same?", a: "Yes — pump output, CFM and pressure are identical for a matched pair. Only footprint and ceiling clearance differ." },
    ],
    amazonQuery: "horizontal air compressor",
  },
  {
    slug: "twin-cylinder-air-compressors",
    title: "Twin Cylinder Air Compressors",
    shortLabel: "Twin Cylinder",
    group: "type",
    h1: `Best Twin Cylinder Air Compressors UK ${Y}`,
    metaTitle: `Best Twin Cylinder Air Compressors UK ${Y} | V-Twin Compressor Reviews`,
    metaDescription:
      "Independent UK reviews of twin cylinder (V-twin) air compressors. Higher CFM and longer duty cycles for workshops, body shops and trade users.",
    intro:
      "Twin cylinder air compressors use two pistons in a V or in-line layout to roughly double the CFM of a single-cylinder pump at the same RPM. They're the right call for sustained spray painting, sandblasting and high-volume impact work.",
    longCopy: [
      "A V-twin pump runs cooler than a comparable single-cylinder thanks to better airflow over both heads, and produces less pulsation in the output air — handy for spraying.",
      "Most twin-cylinder UK compressors are belt-drive with 3HP–5.5HP motors, delivering 12–25 CFM. Two-stage twin-cylinder models hit 12–15 bar for high-pressure applications.",
      "Top UK picks: Hyundai HY3150V, SIP Airmate ISBD11/200, Clarke SE16C200V, Sealey SAC52775B.",
    ],
    faqs: [
      { q: "Twin cylinder vs single cylinder air compressor?", a: "Twin cylinder roughly doubles CFM and runs cooler under load, making it the better choice for sustained high-demand tools. Single cylinder is fine for intermittent DIY." },
      { q: "Are twin-cylinder pumps two-stage?", a: "Not always — many twin pumps are still single-stage. True two-stage twins have one cylinder feeding the other for pressures above 12 bar." },
      { q: "Do twin cylinder compressors need bigger tanks?", a: "Yes — match a twin pump's CFM to at least a 100L tank to make the most of its output and reduce cycling." },
    ],
    amazonQuery: "twin cylinder air compressor",
  },
  {
    slug: "compact-air-compressors",
    title: "Compact Air Compressors",
    shortLabel: "Compact",
    group: "type",
    h1: `Best Compact Air Compressors UK ${Y}`,
    metaTitle: `Best Compact Air Compressors UK ${Y} | Small Compressor Reviews`,
    metaDescription:
      "Reviews of compact air compressors — small, lightweight units perfect for tyre inflation, brad nailers, hobbyists and tight workshop spaces.",
    intro:
      "Compact air compressors are built for users who need air without surrendering a square metre of garage floor. From 6L pancake units to slim 12L hotdog tanks, these models are the cleanest, quietest entry point into compressed air.",
    longCopy: [
      "Compact compressors typically weigh under 15 kg, run on a standard 13A plug and deliver 1.5–3 CFM — more than enough for trim nailing, tyre inflation, airbrushing and small impact work.",
      "The vast majority are oil-free direct-drive pumps in a steel or plastic clamshell, with rubber feet and a carry handle. Step up to a silent compact model if you'll use it indoors.",
      "Best UK compact picks: DeWalt DPC6MRC, Bostitch BTFP02012, Makita MAC2400, Hyundai HY6508, Clarke Bandit IV.",
    ],
    faqs: [
      { q: "What can I run with a compact air compressor?", a: "Brad nailers, staplers, tyre inflators, airbrushes, small dusters and basic impact wrenches — anything needing under 3 CFM at 90 PSI." },
      { q: "Are compact compressors any good for cars?", a: "Yes for tyre top-ups and small impact wrench work, but a compact unit will struggle with sustained impact work on rusted-on bolts or full panel spraying." },
      { q: "Do compact air compressors need oil?", a: "Almost all are oil-free with no servicing required beyond draining the condensate from the tank weekly." },
    ],
    amazonQuery: "compact air compressor",
  },
  {
    slug: "heavy-duty-air-compressors",
    title: "Heavy-Duty Air Compressors",
    shortLabel: "Heavy-Duty",
    group: "type",
    h1: `Best Heavy-Duty Air Compressors UK ${Y}`,
    metaTitle: `Best Heavy-Duty Air Compressors UK ${Y} | Industrial Compressor Reviews`,
    metaDescription:
      "Independent UK reviews of heavy-duty air compressors. Compare large-tank, high-CFM belt-drive and screw compressors for trade, industrial and commercial use.",
    intro:
      "Heavy-duty air compressors are built for users who need air all day, every day. Expect cast-iron pumps, 150L+ tanks, 3HP+ motors and 75–100% duty cycles — the kind of kit that anchors a working garage, body shop or fabrication unit.",
    longCopy: [
      "Heavy-duty compressors run at lower RPMs, with thicker pump castings and longer service intervals than DIY-grade models. Expect 15–25 CFM at 8 bar, 12–15 bar two-stage capability and tanks from 150L to 500L.",
      "Most heavy-duty compressors in the UK are belt-drive piston or rotary screw. Three-phase versions are common above 4kW. Plan on a dedicated wired circuit and a moisture trap on the output.",
      "Top UK heavy-duty picks: SIP Airmate ISBD15/270, Hyundai HY3150V, ABAC PRO B6000, Sealey SAC52775B.",
    ],
    faqs: [
      { q: "What makes a compressor ‘heavy-duty’?", a: "A duty cycle of 75% or higher, a cast-iron pump, 100L+ tank and a service life of 5,000+ hours typically defines a heavy-duty industrial compressor." },
      { q: "Do I need three-phase for a heavy-duty compressor?", a: "Above ~3.5kW (about 18 CFM), three-phase 415V is standard. Single-phase heavy-duty models exist but are more expensive." },
      { q: "How often do heavy-duty compressors need servicing?", a: "Plan on quarterly oil checks, an annual oil change, belt inspection every 500 hours and intake filter replacements every 6 months." },
    ],
    amazonQuery: "heavy duty air compressor",
  },

  // ============ BY USE CASE ============
  {
    slug: "air-compressors-for-spray-painting",
    title: "Air Compressors for Spray Painting",
    shortLabel: "Spray Painting",
    group: "use",
    h1: `Best Air Compressors for Spray Painting UK ${Y}`,
    metaTitle: `Best Air Compressors for Spray Painting UK ${Y} | Reviews`,
    metaDescription:
      "Which air compressor for spray painting? Independent UK reviews and CFM advice for HVLP, LVLP and conventional spray guns on cars, furniture and trim.",
    intro:
      "Spray painting is one of the most CFM-hungry jobs you can throw at a compressor — get the size wrong and you'll suffer streaky finishes, sputtering guns and runs in the paint. The right compressor matches your spray gun's exact air demand at its working pressure.",
    longCopy: [
      "Look up your spray gun's spec sheet — most HVLP guns need 9–14 CFM at 29 PSI, conventional guns can demand 15+ CFM, while small detail and trim guns may only need 3–5 CFM.",
      "Size the compressor to deliver 150% of the gun's CFM at the gun's working pressure, with a 50L+ tank to even out demand. For sustained car panel work, a 200L belt-drive compressor with a 16+ CFM pump is the sweet spot.",
      "Always add a moisture trap, oil/water separator and a regulator at the gun — moist or oily air ruins paint finishes, especially with single-stage paints.",
    ],
    faqs: [
      { q: "What size compressor for spray painting a car?", a: "A 50–100 gallon (200–380L) belt-drive compressor with 16–20 CFM at 90 PSI is ideal for full car panels. For touch-ups and small jobs, a 50L unit with 10+ CFM is workable." },
      { q: "Can I spray paint with a small compressor?", a: "Yes for airbrushing, trim and aerosol-style detail work using LVLP guns drawing 3–5 CFM. Full panel work needs 12+ CFM." },
      { q: "Do I need an oil-free compressor for spray painting?", a: "No — oil-lubricated compressors with a proper oil/water separator at the gun deliver clean dry air. Oil-free compressors are slightly more forgiving if the separator fails." },
    ],
    amazonQuery: "air compressor for spray painting",
  },
  {
    slug: "air-compressors-for-nail-guns",
    title: "Air Compressors for Nail Guns",
    shortLabel: "Nail Guns",
    group: "use",
    h1: `Best Air Compressors for Nail Guns UK ${Y}`,
    metaTitle: `Best Air Compressors for Nail Guns UK ${Y} | Reviews & CFM Guide`,
    metaDescription:
      "The right air compressor for brad, finish and framing nailers. Independent UK CFM advice and reviews for first-fix and second-fix carpentry.",
    intro:
      "Pairing the right compressor with your nailer is the difference between a smooth working day and constant cycling. Brad and finish nailers need very little — but framing nailers can demand serious volume.",
    longCopy: [
      "Brad nailers and 18-gauge finish nailers typically use 0.3–0.5 CFM per shot, easily fed by a 6L pancake compressor. Second-fix 16- and 15-gauge finish nailers want 0.5–1.0 CFM. Framing nailers can demand 2.5–4 CFM at full bore.",
      "For occasional DIY trim work, a quiet 9–12L oil-free portable compressor is perfect. For first-fix framing all day, step up to a 24–50L wheeled compressor with 4+ CFM.",
      "Make sure the compressor delivers at least the nailer's required PSI (usually 60–120 PSI). Set the regulator to the nailer's recommended pressure to avoid over-driving fasteners.",
    ],
    faqs: [
      { q: "What size air compressor for a brad nailer?", a: "A 6L pancake compressor with 1.5+ CFM is more than enough for any 18-gauge brad nailer." },
      { q: "Can I run a framing nailer off a small compressor?", a: "Yes for intermittent use, but a 6L pancake will cycle constantly under sustained framing. Use a 24L+ portable for proper site work." },
      { q: "Does compressor pressure matter for nailing?", a: "Set the regulator to the nailer's stamped operating pressure — typically 80–120 PSI. Higher pressures damage the nailer; lower pressures fail to seat nails." },
    ],
    amazonQuery: "air compressor nail gun",
  },
  {
    slug: "air-compressors-for-tyre-inflation",
    title: "Air Compressors for Tyre Inflation",
    shortLabel: "Tyre Inflation",
    group: "use",
    h1: `Best Air Compressors for Tyre Inflation UK ${Y}`,
    metaTitle: `Best Air Compressors for Tyre Inflation UK ${Y} | Reviews`,
    metaDescription:
      "Car, van, motorcycle and bike — the best air compressors for tyre inflation in the UK. Independent reviews of mains and 12V models.",
    intro:
      "Tyre inflation is a low-CFM job, so a compact compressor goes a long way. Whether you want a permanent garage solution or a 12V plug-in for the boot, there's a perfect fit at every budget.",
    longCopy: [
      "For at-home use, any 6L pancake compressor with 1.5+ CFM at 90 PSI will inflate four car tyres in under a minute. Add a quick-release coupler and a tyre inflator gauge for the easiest workflow.",
      "For van and 4×4 owners running larger 35 PSI+ tyres, choose a 24L portable with 3+ CFM. For mobile use on the road, a 12V plug-in inflator capable of 35–50 PSI handles emergency top-ups.",
      "Trade tyre fitters need 4–6 CFM at 100 PSI to drive ½″ impact wrenches as well as inflate — plan on a 24L wheeled compressor or larger.",
    ],
    faqs: [
      { q: "What size compressor for car tyres?", a: "Almost any compressor delivering 90+ PSI will inflate a car tyre. For convenience, a 6L pancake is plenty." },
      { q: "Are 12V tyre inflators any good?", a: "Yes for emergency use and weekly tyre checks. Most quality 12V inflators reach 35–50 PSI but are slower and louder than mains compressors." },
      { q: "Can a tyre inflator run impact wrenches?", a: "No — 12V tyre inflators don't have the CFM to drive air tools. You need at least a 24L mains compressor for impact work." },
    ],
    amazonQuery: "air compressor tyre inflator",
  },
  {
    slug: "air-compressors-for-car-garages",
    title: "Air Compressors for Car Garages",
    shortLabel: "Car Garages",
    group: "use",
    h1: `Best Air Compressors for Car Garages UK ${Y}`,
    metaTitle: `Best Air Compressors for Car Garages UK ${Y} | Reviews`,
    metaDescription:
      "Workshop-grade air compressor reviews for car garages. Compare 50L–270L belt-drive and screw compressors for impact wrenches, spray painting and sandblasting.",
    intro:
      "A car garage compressor has to handle everything from a single tyre top-up to a full afternoon of spray painting. That's a tall order — and it's why most pro garages run 100L+ belt-drive units or rotary screw compressors.",
    longCopy: [
      "Size the compressor to your heaviest single tool — usually a ½″ impact wrench (4–7 CFM) or a spray gun (10–18 CFM). For mixed use, a 150–200L belt-drive 3HP unit at 14–18 CFM covers 90% of jobs.",
      "Add a refrigerated air dryer or an inline desiccant tower for spray work — atmospheric humidity in a UK garage is more than enough to ruin a paint job.",
      "Strong UK picks: SIP Airmate ISBD11/200, Hyundai HY3150V, Clarke SE16C200V, Sealey SAC52775B.",
    ],
    faqs: [
      { q: "What CFM do I need for a car garage compressor?", a: "Plan on 14–18 CFM at 8 bar for a single-bay independent garage running impact, blowing, spraying and sanding." },
      { q: "Should a car garage use a screw or piston compressor?", a: "Piston belt-drive is fine for 1–2 bay garages. Three-bay-plus body shops typically move to a rotary screw to keep up with continuous spray and sanding demand." },
      { q: "Do I need a refrigerated air dryer in a UK garage?", a: "For spray painting, yes — UK ambient humidity often condenses water in tank air. For impact tools and tyre work an inline trap is fine." },
    ],
    amazonQuery: "garage air compressor",
  },
  {
    slug: "air-compressors-for-home-workshops",
    title: "Air Compressors for Home Workshops",
    shortLabel: "Home Workshops",
    group: "use",
    h1: `Best Air Compressors for Home Workshops UK ${Y}`,
    metaTitle: `Best Air Compressors for Home Workshops UK ${Y} | Reviews`,
    metaDescription:
      "The best home workshop air compressors in the UK reviewed. Reliable, low-noise 24L–100L models for DIY mechanics, woodworkers and hobbyists.",
    intro:
      "Home workshop users don't need industrial-grade air — but they do need reliability, low noise and enough CFM to power the most common tools without constant cycling.",
    longCopy: [
      "A 50L oil-lubricated belt-drive compressor delivering 8–10 CFM at 8 bar will run almost every home workshop tool: impact wrench, ratchet, sander, brad nailer and small spray gun. For pure DIY use, a 24L oil-free direct-drive unit at 6 CFM is a great budget pick.",
      "Choose a silent or low-noise model if your workshop shares walls with living spaces — modern silent compressors run 55–65 dB, no louder than a dishwasher.",
      "Top UK home workshop picks: SIP Airmate Tornado, Hyundai HY24-100, Clarke Bandit Plus, Sealey SAC1153B.",
    ],
    faqs: [
      { q: "What size compressor for a home workshop?", a: "A 24–50L compressor with 6–8 CFM covers nearly all home workshop tasks including impact tools, brad nailing and touch-up spraying." },
      { q: "Should home workshop compressors be oil or oil-free?", a: "Oil-free models are easier to live with — no oil changes, no oil mist in the air. Oil-lubricated belt-drive units last longer if you'll do hours of work each week." },
      { q: "How loud is too loud for a domestic garage?", a: "Above 85 dB is uncomfortable without hearing protection. Look for silent models at 60–70 dB if your workshop is close to bedrooms or neighbours." },
    ],
    amazonQuery: "home workshop air compressor",
  },
  {
    slug: "air-compressors-for-mechanics",
    title: "Air Compressors for Mechanics",
    shortLabel: "Mechanics",
    group: "use",
    h1: `Best Air Compressors for Mechanics UK ${Y}`,
    metaTitle: `Best Air Compressors for Mechanics UK ${Y} | Workshop Reviews`,
    metaDescription:
      "Independent UK reviews of air compressors for mechanics. Compare impact-friendly 100L–270L belt-drive compressors for trade and pro use.",
    intro:
      "Mechanics' air compressors live a hard life — impact wrenches, ratchets, blowguns, paint touch-ups and grinders all running daily. Reliability, duty cycle and CFM headroom matter more than headline pressure.",
    longCopy: [
      "Plan on 12–18 CFM at 8 bar to comfortably run a ½″ impact wrench and small angle grinder simultaneously. A 100–200L belt-drive twin-cylinder compressor is the standard pro mechanic setup.",
      "Add an inline water trap, regulator and filter (FRL) close to each tool drop. Mechanics' tools are sensitive to water and oil — keep both out of the lines for long tool life.",
      "Best UK mechanic compressors: SIP Airmate ISBD11/200, Hyundai HY3150V, Clarke SE16C200V, Sealey SAC52775B.",
    ],
    faqs: [
      { q: "What CFM do impact wrenches need?", a: "½″ impacts typically draw 4–7 CFM; ¾″ impacts need 7–10 CFM; 1″ heavy-truck impacts can demand 12+ CFM." },
      { q: "Are silent compressors OK for mechanics?", a: "Yes for tyre, blowing and ratchet work. For heavy impact and full-day use, a belt-drive workshop compressor outperforms most silent units." },
      { q: "Do mechanics need two-stage compressors?", a: "Two-stage compressors hit 12–15 bar, useful for high-torque tyre rattle-guns and pro impact tools. Single-stage at 10 bar handles most general mechanic work." },
    ],
    amazonQuery: "mechanic air compressor",
  },
  {
    slug: "air-compressors-for-diy",
    title: "Air Compressors for DIY",
    shortLabel: "DIY",
    group: "use",
    h1: `Best Air Compressors for DIY UK ${Y}`,
    metaTitle: `Best Air Compressors for DIY UK ${Y} | Reviews & Buying Guide`,
    metaDescription:
      "The best DIY air compressors in the UK reviewed. Affordable 6L–50L oil-free and silent models for home, garage and weekend projects.",
    intro:
      "DIY users want a compressor that's affordable, low-maintenance, quiet enough not to upset the neighbours and powerful enough for tyres, nailers, blowing and the occasional impact wrench.",
    longCopy: [
      "For most DIYers, a 24L oil-free direct-drive compressor delivering 5–6 CFM at 8 bar is the sweet spot — under £150, covers 95% of jobs, and needs zero servicing beyond a weekly tank drain.",
      "Step up to a 50L belt-drive if you'll do regular spray work or sustained impact wrench use. Choose a silent compact model if you'll work indoors near family or in a flat.",
      "Top UK DIY picks: SIP Airmate Tornado 7/24, Hyundai HY24-100, Clarke Bandit IV, Sealey SAC1153B.",
    ],
    faqs: [
      { q: "What size compressor for DIY?", a: "A 24L oil-free direct-drive unit with 5–6 CFM at 90 PSI is plenty for tyres, nail guns, brad nailers and intermittent impact work." },
      { q: "Are oil-free DIY compressors any good?", a: "Yes — for the 1–10 hours a week most DIY users run a compressor, oil-free models offer better value, lower maintenance and cleaner air than oil-lubricated equivalents." },
      { q: "Do I need a silent compressor for DIY?", a: "Highly recommended if you'll work in an attached garage, indoors or in a flat — silent units run at half the perceived loudness of standard models." },
    ],
    amazonQuery: "diy air compressor",
  },
  {
    slug: "air-compressors-for-woodworking",
    title: "Air Compressors for Woodworking",
    shortLabel: "Woodworking",
    group: "use",
    h1: `Best Air Compressors for Woodworking UK ${Y}`,
    metaTitle: `Best Air Compressors for Woodworking UK ${Y} | Reviews`,
    metaDescription:
      "Independent UK reviews of air compressors for woodworking. Compare quiet 6L–50L models perfect for brad nailing, finish work, dusters and HVLP staining.",
    intro:
      "Woodworking air compressors live indoors with sawdust and finish chemicals, so they need to be quiet, oil-free and clean-running. Most fine woodworkers settle on a silent direct-drive compressor or a quiet belt-drive at 50–100L.",
    longCopy: [
      "Trim and finish work needs very little CFM — 1.5–2 CFM covers brad and pin nailers comfortably. Step up to 3+ CFM if you'll use an HVLP gun for stains, shellac or lacquer on cabinet work.",
      "Choose oil-free to keep oil mist out of your finish work, and a silent model (55–65 dB) so you can hear yourself think in a small shop.",
      "Top UK woodworking picks: Bambi BB24 silent, Hyundai HY7524 silent, DeWalt DPC6MRC, Bostitch BTFP02012.",
    ],
    faqs: [
      { q: "What size compressor for woodworking?", a: "A 6–24L oil-free silent compressor with 1.5–3 CFM covers all routine cabinetmaking — nailers, dusters and small HVLP guns." },
      { q: "Do I need an oil-free compressor for finish work?", a: "Strongly recommended — oil mist from a cheap oil-lubricated compressor can contaminate stains and lacquers, causing fisheyes and dull patches." },
      { q: "Are silent compressors strong enough for woodworking?", a: "Yes — modern silent compressors deliver 1.5–4 CFM, which is plenty for every common woodworking task." },
    ],
    amazonQuery: "woodworking air compressor",
  },
  {
    slug: "air-compressors-for-sandblasting",
    title: "Air Compressors for Sandblasting",
    shortLabel: "Sandblasting",
    group: "use",
    h1: `Best Air Compressors for Sandblasting UK ${Y}`,
    metaTitle: `Best Air Compressors for Sandblasting UK ${Y} | Reviews`,
    metaDescription:
      "The right air compressor for sandblasting. UK reviews and CFM guide for cabinet, pot and bench-top blasters.",
    intro:
      "Sandblasting is one of the most CFM-hungry workshop jobs you can attempt. Undersize the compressor and your blast nozzle will starve, your pressure will drop, and you'll spend the day cleaning a single panel.",
    longCopy: [
      "Tiny 1.5mm cabinet blast nozzles draw around 5 CFM at 80 PSI. Step up to a 4–5mm pot blaster nozzle and you'll demand 15–25 CFM continuously. For sustained large-area blasting, a rotary screw compressor or a twin-stack of belt-drive units is normal.",
      "Always run an in-line moisture trap and desiccant dryer — wet abrasive blocks instantly. A larger tank (200L+) lets the compressor catch up between bursts.",
      "Strong UK picks for sandblasting include the Hyundai HY3150V, SIP Airmate ISBD15/270 and any 7.5–11kW rotary screw compressor.",
    ],
    faqs: [
      { q: "What size compressor for sandblasting?", a: "Small cabinet blasters need 5–8 CFM; a 4mm pot blaster needs 18–25 CFM continuously. Match the compressor to your largest blast nozzle." },
      { q: "Can I sandblast with a DIY compressor?", a: "Only with the smallest 1.5–2mm nozzles for short bursts. Anything larger or sustained will overwhelm a typical 24L direct-drive unit." },
      { q: "Do I need a screw compressor for sandblasting?", a: "Not always — a 270L twin-cylinder belt-drive compressor can keep up with most 4mm pot blasters. Screws win when you'll blast for hours without stopping." },
    ],
    amazonQuery: "air compressor for sandblasting",
  },
  {
    slug: "air-compressors-for-roofing",
    title: "Air Compressors for Roofing",
    shortLabel: "Roofing",
    group: "use",
    h1: `Best Air Compressors for Roofing UK ${Y}`,
    metaTitle: `Best Air Compressors for Roofing UK ${Y} | Reviews`,
    metaDescription:
      "Reviews of air compressors for roofing in the UK. Compare wheeled portable compressors for coil nailers, slate stapling and rapid-cycle work.",
    intro:
      "Roofing compressors live in the back of a van, on rough ground and in the rain. They need to be tough, portable, and capable of feeding a coil nailer at full speed.",
    longCopy: [
      "Coil roofing nailers typically draw 1.5–3 CFM. A pair of roofers sharing one compressor will need 4–6 CFM and a tank of at least 24L to avoid waiting for the pressure to rebuild.",
      "Choose a wheeled portable with a steel rollcage, oil-lubricated belt-drive pump for site durability, and an electric start regulator that holds your nailer's exact pressure.",
      "UK roofing favourites: Bostitch RC-10-U, Senco PC1010, Hyundai HY7524-50, Clarke Bandit Plus.",
    ],
    faqs: [
      { q: "What size compressor for a coil roofing nailer?", a: "A 24L portable with 3 CFM is perfect for a single nailer. Two nailers on the same compressor want 50L and 5+ CFM." },
      { q: "Are roofing compressors weather-resistant?", a: "Most have IP-rated motor housings but should still be sheltered from direct rain. A simple tarp or van storage keeps them lasting." },
      { q: "Petrol or electric compressor for roofing?", a: "Electric is standard for UK roof work, plugged into a site transformer. Petrol compressors are common in remote or off-grid jobs." },
    ],
    amazonQuery: "roofing air compressor",
  },
  {
    slug: "air-compressors-for-airbrushing",
    title: "Air Compressors for Airbrushing",
    shortLabel: "Airbrushing",
    group: "use",
    h1: `Best Air Compressors for Airbrushing UK ${Y}`,
    metaTitle: `Best Air Compressors for Airbrushing UK ${Y} | Reviews`,
    metaDescription:
      "Independent UK reviews of airbrush compressors. Compare silent, low-pressure compressors for model painters, illustrators, makeup artists and cake decorators.",
    intro:
      "Airbrushing compressors are a world apart from workshop compressors: tiny, silent, low-pressure and tuned for fine continuous airflow. The right unit lets you work for hours without fatigue or pulsation in the spray.",
    longCopy: [
      "Most airbrushes draw 0.3–0.8 CFM at 15–40 PSI — well within the range of a small 3L mini compressor. Look for a model with a pressure regulator, moisture trap and auto-stop pressure switch.",
      "Diaphragm compressors are silent but pulse the air slightly. Piston mini compressors with a 3L tank give a steadier flow, ideal for serious illustration and miniature painting.",
      "Top UK airbrush compressors: Iwata Smart Jet, Sparmax TC-501N, AS186 (and variants), Bambi 5L silent.",
    ],
    faqs: [
      { q: "Do I need a tank on my airbrush compressor?", a: "A 3L tank smooths airflow and lets the pump rest between shots — strongly recommended for portrait, miniature or production work." },
      { q: "What PSI for airbrushing?", a: "Most acrylic and ink work runs at 15–30 PSI. Heavy-bodied paints and stencil work may go up to 40 PSI." },
      { q: "Can I use a workshop compressor for airbrushing?", a: "Yes with a regulator, moisture trap and oil filter — but a dedicated airbrush compressor is quieter, smaller and far more pleasant for studio use." },
    ],
    amazonQuery: "airbrush compressor",
  },
  {
    slug: "air-compressors-for-impact-wrenches",
    title: "Air Compressors for Impact Wrenches",
    shortLabel: "Impact Wrenches",
    group: "use",
    h1: `Best Air Compressors for Impact Wrenches UK ${Y}`,
    metaTitle: `Best Air Compressors for Impact Wrenches UK ${Y} | Reviews`,
    metaDescription:
      "The right air compressor for impact wrenches. UK reviews and CFM guide for ½″, ¾″ and 1″ pneumatic impact guns.",
    intro:
      "Impact wrench performance lives or dies on compressor CFM. The trigger pulls a sudden burst of air, and if the compressor can't keep up, your torque numbers drop fast.",
    longCopy: [
      "Plan on 4–7 CFM at 90 PSI for ½″ impact wrenches, 7–10 CFM for ¾″ and 10–15 CFM for 1″ heavy-duty trucks impacts. Match the compressor to the impact, not the other way around.",
      "A 50L tank smooths the demand of repeated impact bursts and lets the compressor catch up. Sustained tyre or chassis work benefits from a 100L+ belt-drive unit.",
      "Top picks: SIP Airmate ISBD11/100, Hyundai HY30100, Clarke SE16C150, Sealey SAC2503B.",
    ],
    faqs: [
      { q: "What CFM does a ½″ impact wrench need?", a: "Most ½″ impact wrenches need 4–7 CFM at 90 PSI for full torque. Light-duty models can manage on 3 CFM but with reduced break-away torque." },
      { q: "Can I use a 6L compressor for an impact wrench?", a: "For light occasional use yes — but the compressor will cycle constantly and the impact will lose torque under heavy bolts." },
      { q: "What size tank for impact wrench work?", a: "50L minimum for tyre work; 100L+ for sustained chassis or fleet work." },
    ],
    amazonQuery: "air compressor for impact wrench",
  },
  {
    slug: "air-compressors-for-plasma-cutters",
    title: "Air Compressors for Plasma Cutters",
    shortLabel: "Plasma Cutters",
    group: "use",
    h1: `Best Air Compressors for Plasma Cutters UK ${Y}`,
    metaTitle: `Best Air Compressors for Plasma Cutters UK ${Y} | Reviews`,
    metaDescription:
      "Independent UK reviews and CFM guide for plasma cutter air compressors. Compare clean-air models for inverter plasma at 30A, 40A and 60A.",
    intro:
      "Plasma cutters need clean, dry, properly-sized air supply. Get it wrong and you'll burn out consumables in hours; get it right and a plasma cutter delivers years of fast, accurate cutting.",
    longCopy: [
      "Most 30–40A plasma cutters need 4–6 CFM at 60–80 PSI. 60A+ machines demand 7–12 CFM. Always run the compressor 25% above the plasma's spec for headroom.",
      "Clean dry air is non-negotiable — water or oil in the air destroys plasma torch consumables fast. Use an inline moisture trap, an oil filter and a refrigerated dryer if you'll cut all day.",
      "Top picks: SIP Airmate ISBD11/100, Hyundai HY3050V, Clarke SE16C150, Sealey SAC52775B.",
    ],
    faqs: [
      { q: "What size compressor for a 40A plasma cutter?", a: "Plan on 5–6 CFM at 80 PSI minimum. A 50L belt-drive compressor handles this comfortably." },
      { q: "Why does my plasma cutter eat consumables?", a: "Almost always moisture or oil in the air supply. Add a desiccant dryer and oil coalescing filter — consumables can last 5–10× longer." },
      { q: "Can I use an oil-free compressor with plasma?", a: "Yes — and many fabricators prefer it because there's no oil to worry about in the air supply." },
    ],
    amazonQuery: "air compressor for plasma cutter",
  },
  {
    slug: "air-compressors-for-agricultural-use",
    title: "Air Compressors for Agricultural Use",
    shortLabel: "Agricultural",
    group: "use",
    h1: `Best Air Compressors for Agricultural Use UK ${Y}`,
    metaTitle: `Best Air Compressors for Agricultural Use UK ${Y} | Reviews`,
    metaDescription:
      "Reviews of air compressors for farm and agricultural use in the UK. Tough, weatherproof, high-CFM compressors for workshops, tyre work and PTO operation.",
    intro:
      "Farm air compressors run in barns, workshops and field trailers. They need to be robust, weather-tolerant, and capable of running everything from a tractor tyre rattle gun to a high-pressure washer feed.",
    longCopy: [
      "Most working farms run a 100–270L belt-drive unit with a 3HP+ motor delivering 12–18 CFM. That's enough for ¾″ impact wrenches on tractor wheels, 2″ impacts on heavy machinery and intermittent paint work on implements.",
      "PTO-driven and engine-driven compressors are common on dairy and arable farms for field servicing — look for Hyundai PTO units and Sealey diesel compressor packs.",
      "For most UK farms, a Hyundai HY3150V, SIP Airmate ISBD11/200 or Clarke SE16C200V mounted in the main workshop is the sensible choice.",
    ],
    faqs: [
      { q: "What size compressor for a farm workshop?", a: "Plan on 14–18 CFM for general farm workshop use — enough to drive ¾″ impacts and small spray jobs on machinery." },
      { q: "Do farms use diesel or electric compressors?", a: "Mostly electric (3-phase) in the main workshop; diesel or PTO compressors for field servicing on remote machinery." },
      { q: "Are farm compressors weatherproofed?", a: "Standard compressors should still be kept under cover. PTO and engine-driven units are designed for outdoor use." },
    ],
    amazonQuery: "agricultural air compressor",
  },
  {
    slug: "air-compressors-for-mobile-mechanics",
    title: "Air Compressors for Mobile Mechanics",
    shortLabel: "Mobile Mechanics",
    group: "use",
    h1: `Best Air Compressors for Mobile Mechanics UK ${Y}`,
    metaTitle: `Best Air Compressors for Mobile Mechanics UK ${Y} | Reviews`,
    metaDescription:
      "Independent UK reviews of compressors for mobile mechanics. Compare van-mounted and portable compressors with impact-wrench-grade CFM.",
    intro:
      "Mobile mechanics need a compressor that lives in a van, runs on a 13A plug or 12V, and delivers enough CFM to undo lugs on the side of a road. It's a tough brief — but several models excel at it.",
    longCopy: [
      "For van-mounted use, a 50L wheeled belt-drive at 8–10 CFM is the gold standard. It plugs into a 13A site supply or an inverter pack, and runs a ½″ impact long enough for full wheel changes.",
      "For lighter mobile use (tyre top-ups, brake bleeding, blowing), a compact 24L portable at 5–6 CFM is plenty. For emergency roadside work, a heavy-duty 12V mobile compressor can run quick-release jobs without mains power.",
      "Top mobile picks: Hyundai HY7524, SIP Airmate Tornado 7/50, Sealey SAC5030E, Clarke Bandit Plus.",
    ],
    faqs: [
      { q: "What size compressor fits a van?", a: "A 24–50L wheeled portable is the sweet spot — small enough to lift, big enough for serious tools." },
      { q: "Can I run a mobile compressor off an inverter?", a: "Yes, with a 2500W+ pure-sine inverter and a deep-cycle battery bank. Cycling current is the limit — most 2HP compressors hit 12–15A at start-up." },
      { q: "Are 12V compressors enough for mobile mechanics?", a: "12V compressors are fine for tyre work but lack the CFM for impact wrenches or spray jobs." },
    ],
    amazonQuery: "mobile mechanic air compressor",
  },

  // ============ BY TANK SIZE ============
  {
    slug: "6l-air-compressors",
    title: "6L Air Compressors",
    shortLabel: "6L",
    group: "tank",
    h1: `Best 6L Air Compressors UK ${Y}`,
    metaTitle: `Best 6L Air Compressors UK ${Y} | Compact Tank Reviews`,
    metaDescription:
      "The best 6L air compressors in the UK. Compare lightweight pancake compressors ideal for nail guns, tyre inflation and DIY trim work.",
    intro:
      "6L air compressors — usually pancake or hot-dog format — are the ultimate hand-carry tank size. They're light enough to take up a ladder, quiet enough for indoor work, and powerful enough for most second-fix and DIY tasks.",
    longCopy: [
      "Expect 1.5–3 CFM, 8–10 bar, oil-free direct-drive pumps and a working weight of 8–14 kg. Most 6L compressors run on a standard 13A plug and start cycling immediately on demand.",
      "Best for: brad and finish nailing, tyre inflation, airbrushing, light impact work, dusting and tool blowing. Not suited for sustained spray painting or sandblasting.",
      "Top UK 6L picks: DeWalt DPC6MRC, Bostitch BTFP02012, Makita MAC2400, Hyundai HY6508.",
    ],
    faqs: [
      { q: "What can a 6L compressor do?", a: "Brad nailing, finish nailing, tyre top-ups, airbrushing, blowing and very small impact wrench work — anything needing under 2 CFM continuously." },
      { q: "Is a 6L compressor enough for spray painting?", a: "Only for very small touch-ups with an LVLP gun. A 6L tank will starve any HVLP or conventional spray gun fast." },
      { q: "Are 6L compressors loud?", a: "Standard 6L pancakes run 78–85 dB. Silent variants are now available at 55–65 dB for indoor work." },
    ],
    amazonQuery: "6L air compressor",
  },
  {
    slug: "8l-air-compressors",
    title: "8L Air Compressors",
    shortLabel: "8L",
    group: "tank",
    h1: `Best 8L Air Compressors UK ${Y}`,
    metaTitle: `Best 8L Air Compressors UK ${Y} | Lightweight Compressor Reviews`,
    metaDescription:
      "Reviews of the best 8L air compressors in the UK. Compact tanks ideal for DIY, nail guns and tyre inflation.",
    intro:
      "8L air compressors split the difference between hand-carry 6L pancakes and shoulder-carry 12L hotdogs — perfect for trim carpenters, light DIY and any user who wants slightly longer run-time between cycles.",
    longCopy: [
      "Most 8L compressors are oil-free direct-drive at 2 HP, delivering 2–3 CFM at 8 bar. Expect a working weight of 9–16 kg and a noise level of 70–90 dB unless you choose a silent model.",
      "Best for: trim carpentry, occasional tyre work, brad and finish nailing, airbrushing, tyre fitter top-ups.",
      "Top UK 8L picks: Hyundai HY8508, SIP Airmate Tornado 6/8 and Bambi BB8 silent.",
    ],
    faqs: [
      { q: "8L vs 24L air compressor?", a: "8L is more portable and quicker to fill, but cycles more often. 24L gives you longer continuous run-time at the cost of weight and footprint." },
      { q: "Can an 8L compressor run a paint sprayer?", a: "Only for tiny detail work with LVLP guns. Full panel spraying needs 50L+." },
      { q: "Are 8L compressors good for cars?", a: "Yes for tyre work and small impact use. For brake bleeding, sustained impact or panel spraying step up to 24–50L." },
    ],
    amazonQuery: "8L air compressor",
  },
  {
    slug: "24l-air-compressors",
    title: "24L Air Compressors",
    shortLabel: "24L",
    group: "tank",
    h1: `Best 24L Air Compressors UK ${Y}`,
    metaTitle: `Best 24L Air Compressors UK ${Y} | Reviews & Buying Guide`,
    metaDescription:
      "The best 24L air compressors in the UK. Mid-size portable tanks ideal for impact wrenches, brad nailing and DIY car work.",
    intro:
      "24L is the most popular DIY and trade portable tank size in the UK. It's big enough to run an impact wrench through a full tyre change, small enough to fit in a van, and affordable enough for hobby users.",
    longCopy: [
      "Most 24L compressors are direct-drive oil-free at 2–3 HP, delivering 5–8 CFM at 8 bar. Wheeled and shoulder-carry formats are both common.",
      "Best for: impact wrench work, brad and finish nailing, tyre fitting, occasional spray work, dusters and short-burst sanding.",
      "Top UK 24L picks: SIP Airmate Tornado 7/24, Hyundai HY24-100, Clarke Bandit Plus, Sealey SAC2403E.",
    ],
    faqs: [
      { q: "What can a 24L compressor do?", a: "Most workshop tasks — impact wrenches, nail guns, brad nailers, tyre work, blowing, dusters and occasional spray painting." },
      { q: "Is 24L enough for spray painting?", a: "For small panels and touch-ups yes; for full car panel spraying you'll want 50L+ at 10+ CFM." },
      { q: "How long can a 24L compressor run?", a: "Direct-drive 24L compressors are typically rated for 50% duty cycle — run 30 minutes, rest 30 minutes." },
    ],
    amazonQuery: "24L air compressor",
  },
  {
    slug: "50l-air-compressors",
    title: "50L Air Compressors",
    shortLabel: "50L",
    group: "tank",
    h1: `Best 50L Air Compressors UK ${Y}`,
    metaTitle: `Best 50L Air Compressors UK ${Y} | Reviews & Buying Guide`,
    metaDescription:
      "The best 50L air compressors in the UK reviewed. Compare belt-drive and direct-drive 50-litre models for DIY, trade and small workshop use.",
    intro:
      "50L is the entry point to serious workshop air. Tanks at this size deliver enough headroom for impact wrenches, brad nailing, occasional spray work and tyre fitting — all on a frame that still fits a single-car garage.",
    longCopy: [
      "Expect 6–10 CFM at 8 bar from a 2–3HP belt-drive pump. Oil-lubricated belt-drive 50L compressors offer the longest pump life; oil-free direct-drive variants are quieter and lower-maintenance.",
      "Best for: small workshop use, hobby spray painters, trim carpenters, tyre and brake work, occasional sandblasting.",
      "Top UK 50L picks: SIP Airmate ISBD7/50, Hyundai HY50100, Clarke SE16C50, Sealey SAC5030E.",
    ],
    faqs: [
      { q: "50L vs 100L air compressor?", a: "50L is plenty for DIY and intermittent trade use. Step up to 100L if you'll do sustained spray painting, sandblasting or two-tool workshop work." },
      { q: "Is 50L enough for impact wrenches?", a: "Yes — comfortably runs ½″ impact wrenches for full wheel changes and brake work." },
      { q: "Can I spray a whole car with a 50L compressor?", a: "Yes for primer and small panels with a low-CFM LVLP gun, but full base-coat on the whole car will starve a 50L unit." },
    ],
    amazonQuery: "50L air compressor",
  },
  {
    slug: "100l-air-compressors",
    title: "100L Air Compressors",
    shortLabel: "100L",
    group: "tank",
    h1: `Best 100L Air Compressors UK ${Y}`,
    metaTitle: `Best 100L Air Compressors UK ${Y} | Workshop Compressor Reviews`,
    metaDescription:
      "Reviews of 100-litre air compressors in the UK. Belt-drive workshop compressors for impact wrenches, sandblasting, panel spraying and trade use.",
    intro:
      "100L compressors are the heart of any small workshop or independent garage. Tanks at this size offer enough headroom for sustained spray painting, all-day impact work and small-scale sandblasting.",
    longCopy: [
      "Expect 8–14 CFM at 8 bar from a 2.5–3HP belt-drive cast-iron pump. Two-stage 100L models hit 12 bar for higher-pressure spray and impact tools.",
      "Best for: independent car workshops, body shop touch-ups, professional carpenters and serious DIY spray painters.",
      "Top UK 100L picks: SIP Airmate ISBD11/100, Hyundai HY30100, Clarke SE16C100, Sealey SAC1103B.",
    ],
    faqs: [
      { q: "100L vs 50L compressor?", a: "100L gives you roughly double the run-time per cycle and supports sustained spray painting — worth the extra footprint for trade use." },
      { q: "Is 100L enough for car body work?", a: "For single-bay body work yes, especially with two-stage 12–14 CFM pumps. Multi-bay shops should step up to 200L+." },
      { q: "Single-phase or three-phase for a 100L compressor?", a: "Most 100L compressors are single-phase 240V at up to 3HP. Three-phase units exist for shared workshop use." },
    ],
    amazonQuery: "100L air compressor",
  },
  {
    slug: "150l-air-compressors",
    title: "150L Air Compressors",
    shortLabel: "150L",
    group: "tank",
    h1: `Best 150L Air Compressors UK ${Y}`,
    metaTitle: `Best 150L Air Compressors UK ${Y} | Trade Compressor Reviews`,
    metaDescription:
      "Independent UK reviews of 150-litre air compressors. Mid-size workshop compressors for sustained spray painting, sandblasting and trade use.",
    intro:
      "150L compressors fill the gap between 100L workshop units and 200L+ industrial machines. They're a sweet spot for trade users who want body-shop-grade CFM without committing to 3-phase power.",
    longCopy: [
      "Most UK 150L compressors run a 3HP belt-drive twin-cylinder pump at 12–16 CFM and 8–10 bar. Single-phase 240V is standard.",
      "Best for: serious car body workshops, mobile valeting setups, single-bay garages, trade joiners running multiple tools.",
      "Top UK 150L picks: SIP Airmate ISBD11/150, Hyundai HY3150V, Clarke SE16C150, Sealey SAC1153B.",
    ],
    faqs: [
      { q: "150L vs 200L compressor?", a: "Marginal — both will run almost any single tool. 200L gives slightly more headroom for two tools running at once and is often only £100 more." },
      { q: "Can I spray a whole car with a 150L compressor?", a: "Yes — most 150L 12+ CFM units handle a full base-coat and clear-coat job comfortably." },
      { q: "Do I need three-phase for a 150L compressor?", a: "Most 150L units are single-phase 240V. Three-phase 415V models exist for industrial use." },
    ],
    amazonQuery: "150L air compressor",
  },
  {
    slug: "200l-air-compressors",
    title: "200L Air Compressors",
    shortLabel: "200L",
    group: "tank",
    h1: `Best 200L Air Compressors UK ${Y}`,
    metaTitle: `Best 200L Air Compressors UK ${Y} | Body Shop Compressor Reviews`,
    metaDescription:
      "Independent UK reviews of 200-litre air compressors. Body shop and workshop compressors for sustained spray painting, sandblasting and high-CFM trade work.",
    intro:
      "200L compressors are the standard for body shops and serious workshops. The tank capacity smooths out demand spikes, the pump output keeps spray guns happy, and the duty cycle handles all-day commercial use.",
    longCopy: [
      "Expect 14–20 CFM at 8 bar from a 3–4HP belt-drive twin-cylinder pump. Two-stage 200L models hit 12–14 bar for high-pressure work.",
      "Best for: car body shops, sandblasting, fabrication, multi-tool workshops, motorcycle workshops.",
      "Top UK 200L picks: SIP Airmate ISBD11/200, Hyundai HY3200V, Clarke SE16C200V, Sealey SAC52775B.",
    ],
    faqs: [
      { q: "Is 200L enough for a body shop?", a: "For single-bay body shops yes. Multi-bay or production body shops typically move to a 270L+ or rotary screw compressor." },
      { q: "What HP for a 200L compressor?", a: "3–4HP single-phase or 5HP three-phase is standard. The pump CFM matters more than the headline horsepower." },
      { q: "Vertical or horizontal 200L compressor?", a: "Vertical saves floor space; horizontal is more stable and easier to roll. Performance is identical." },
    ],
    amazonQuery: "200L air compressor",
  },
  {
    slug: "300l-air-compressors",
    title: "300L Air Compressors",
    shortLabel: "300L",
    group: "tank",
    h1: `Best 300L Air Compressors UK ${Y}`,
    metaTitle: `Best 300L Air Compressors UK ${Y} | Industrial Compressor Reviews`,
    metaDescription:
      "UK reviews of 270–300 litre air compressors. Industrial-grade machines for multi-bay body shops, fabrication and continuous sandblasting.",
    intro:
      "270L–300L compressors are firmly in industrial territory. Multi-bay body shops, fabrication units and any workshop running multiple tools at once benefit from the increased reservoir and pump CFM.",
    longCopy: [
      "Most UK 270–300L compressors are three-phase belt-drive twin-cylinder units delivering 18–28 CFM at 10 bar. Two-stage versions reach 12–15 bar for high-pressure applications.",
      "Best for: production body shops, sandblast cabinets, plasma cutting bays, fabrication workshops, motorcycle dealerships.",
      "Top UK 270–300L picks: SIP Airmate ISBD15/270, Hyundai HY30300, Clarke SE26C270, Sealey SAC52775B.",
    ],
    faqs: [
      { q: "Do I need three-phase for a 300L compressor?", a: "Most 270L+ compressors are three-phase 415V. Single-phase 240V versions exist but are limited in pump output." },
      { q: "300L compressor vs rotary screw?", a: "A 300L belt-drive twin-cylinder is fine for 4–6 hours of daily use. Beyond that, a rotary screw is more efficient, quieter and longer-lasting." },
      { q: "How much does a 300L compressor cost?", a: "Quality single-phase 270L compressors run £1,200–£2,000. Three-phase 300L units land at £2,000–£4,500." },
    ],
    amazonQuery: "300L air compressor",
  },

  // ============ BY POWER SOURCE ============
  {
    slug: "electric-air-compressors",
    title: "Electric Air Compressors",
    shortLabel: "Electric",
    group: "power",
    h1: `Best Electric Air Compressors UK ${Y}`,
    metaTitle: `Best Electric Air Compressors UK ${Y} | Mains Compressor Reviews`,
    metaDescription:
      "The best mains-powered electric air compressors in the UK. Reviews of 240V and 415V workshop, DIY and trade compressors.",
    intro:
      "Electric air compressors are the default choice for UK workshops and DIY users — easy to plug in, quiet, low-maintenance and far cleaner than petrol or diesel alternatives.",
    longCopy: [
      "Single-phase 240V electric compressors cover everything from 6L portable units to 200L vertical workshop machines (up to about 3HP). Above 3HP you'll usually move to 415V three-phase for efficiency and torque.",
      "Choose oil-free direct-drive for low-maintenance DIY use, or oil-lubricated belt-drive for sustained workshop duty.",
      "All major UK brands offer electric ranges: Hyundai, SIP, Clarke, Sealey, ABAC, DeWalt, Makita and Bostitch.",
    ],
    faqs: [
      { q: "Are electric air compressors any good?", a: "Yes — for indoor and workshop use they're cheaper to run, quieter and lower-maintenance than petrol or diesel equivalents." },
      { q: "What size electric compressor do I need?", a: "Match CFM to your biggest tool. 6L for nailing; 24L for impact wrenches; 50–100L for spray painting; 150–200L for serious trade workshops." },
      { q: "Single-phase or three-phase electric compressor?", a: "Single-phase 240V for most homes and small workshops up to 3HP. Three-phase 415V for larger workshops and industrial use above 4kW." },
    ],
    amazonQuery: "electric air compressor",
  },
  {
    slug: "petrol-air-compressors",
    title: "Petrol Air Compressors",
    shortLabel: "Petrol",
    group: "power",
    h1: `Best Petrol Air Compressors UK ${Y}`,
    metaTitle: `Best Petrol Air Compressors UK ${Y} | Off-Grid Compressor Reviews`,
    metaDescription:
      "Independent UK reviews of petrol-engine air compressors. Compare site, agricultural and off-grid models from Hyundai, Honda, SIP and Clarke.",
    intro:
      "Petrol air compressors take air to the job — no mains needed. They're the standard tool for construction sites, off-grid agriculture, mobile fence-fitting and remote workshop use.",
    longCopy: [
      "Most UK petrol compressors run a Honda GX or Hyundai-engine pump on a wheeled tank from 50L to 100L. Expect 10–18 CFM at 8 bar — comparable to a single-phase mains belt-drive of similar size.",
      "Best for: building sites, off-grid agriculture, mobile spray work, fence and agricultural fencing teams.",
      "Top UK petrol picks: Hyundai HY3050V-petrol, SIP Airmate Petrol, Clarke Petrol Mobile Air, Sealey SAC4060HE.",
    ],
    faqs: [
      { q: "Are petrol air compressors any good?", a: "Yes for off-grid and site use. They're noisier, heavier and need fuel maintenance compared to electric — but they go anywhere." },
      { q: "How long can a petrol compressor run?", a: "Most have a 6L petrol tank giving 4–6 hours of continuous run-time. Refuel hot or use a long-run auxiliary tank for all-day jobs." },
      { q: "Do petrol compressors need different oil?", a: "Yes — engine oil for the petrol engine (typically SAE 10W-30), separate pump oil for the compressor head." },
    ],
    amazonQuery: "petrol air compressor",
  },
  {
    slug: "diesel-air-compressors",
    title: "Diesel Air Compressors",
    shortLabel: "Diesel",
    group: "power",
    h1: `Best Diesel Air Compressors UK ${Y}`,
    metaTitle: `Best Diesel Air Compressors UK ${Y} | Industrial Mobile Compressors`,
    metaDescription:
      "Independent UK reviews of diesel-engine air compressors. Tow-behind and skid-mounted models for civils, breaker work and large-scale site air.",
    intro:
      "Diesel air compressors are the heavy artillery of mobile air supply. From tow-behind 100 CFM units running multiple breakers, to skid-mounted compressors feeding sandblast pots on bridge maintenance — diesel is the standard for serious off-grid work.",
    longCopy: [
      "Most UK diesel compressors are rotary screw units delivering 60–400+ CFM at 7–14 bar, towed by a Land Rover or HGV. Smaller skid-mounted diesel reciprocating compressors at 20–40 CFM serve mobile fence and agricultural teams.",
      "Best for: civils, road maintenance, breaker work, large-scale sandblasting, remote site air supply, ag fencing.",
      "Top UK diesel picks: Atlas Copco XAS, Doosan, Compair, Hyundai diesel mobiles.",
    ],
    faqs: [
      { q: "How much fuel does a diesel compressor use?", a: "A 100 CFM tow-behind diesel typically burns 2–4 L/hr at full load. Larger 400+ CFM units burn 8–15 L/hr." },
      { q: "Are diesel air compressors louder than petrol?", a: "Smaller diesel skid units are similar to petrol. Large rotary screw diesels with enclosed canopies are surprisingly quiet at 65–75 dB." },
      { q: "Can I tow a diesel compressor with a normal car?", a: "Lighter 100 CFM diesel mobiles weigh around 750 kg — towable by a 4×4 with the right licence. Larger units need an HGV." },
    ],
    amazonQuery: "diesel air compressor",
  },
  {
    slug: "12v-air-compressors",
    title: "12V Air Compressors",
    shortLabel: "12V",
    group: "power",
    h1: `Best 12V Air Compressors UK ${Y}`,
    metaTitle: `Best 12V Air Compressors UK ${Y} | Car Tyre Inflator Reviews`,
    metaDescription:
      "The best 12V air compressors in the UK. Compare car-socket and battery-clamp tyre inflators for cars, 4×4s, motorcycles and vans.",
    intro:
      "12V air compressors plug into a car socket or clip to a battery for portable inflation anywhere. They're not workshop tools — but for tyres, sports balls, paddleboards and air bed-style jobs they're hard to beat for portability.",
    longCopy: [
      "Cheap 12V cigarette-socket inflators reach 35–45 PSI and 5–8L/min — enough for emergency tyre top-ups but slow. Battery-clamp 12V compressors at 100 PSI+ and 50–70 L/min handle 4×4 tyres comfortably.",
      "Look for an auto-stop pressure switch, a real digital gauge (not a wobbly analogue dial), and a large heatsink for back-to-back inflations.",
      "Top UK 12V picks: Ring RAC900, ARB CKMA12, Slime 40026, Michelin 12266.",
    ],
    faqs: [
      { q: "Are 12V air compressors any good?", a: "Yes for tyre inflation, sports balls and inflatables. They're not powerful enough for air tools, brad nailers or spray work." },
      { q: "How long do 12V compressors take to inflate a tyre?", a: "Quality 12V units inflate a 0–32 PSI tyre in 2–4 minutes. Budget cigarette-socket models can take 8–10 minutes." },
      { q: "Can a 12V compressor run off a car battery?", a: "Yes — most plug into a 12V socket. Heavier units (4×4 4WD compressors) clip directly to the battery via crocodile clips for higher current." },
    ],
    amazonQuery: "12V air compressor tyre inflator",
  },
  {
    slug: "cordless-air-compressors",
    title: "Cordless Air Compressors",
    shortLabel: "Cordless",
    group: "power",
    h1: `Best Cordless Air Compressors UK ${Y}`,
    metaTitle: `Best Cordless Air Compressors UK ${Y} | Battery Compressor Reviews`,
    metaDescription:
      "Independent UK reviews of cordless air compressors. Compare 18V, 36V and 54V battery compressors from Makita, DeWalt, Milwaukee and Bosch.",
    intro:
      "Cordless air compressors plug into your existing tool battery platform — no extension lead, no generator, no fuel. They're a game-changer for first-fix carpenters, mobile fence-fitters and anyone tired of dragging cables around a site.",
    longCopy: [
      "Modern 18V and 36V cordless compressors deliver 1.5–3 CFM at 8 bar — enough for brad nailing, finish nailing, tyre top-ups and inflation. Top of the range twin-18V units rival a small mains-powered 24L compressor.",
      "Look for brushless motors, multi-stage pressure regulation and quick-release outlets. Pair with high-Ah batteries (5–6Ah) for serious run-time.",
      "Top cordless picks: Milwaukee M18 FUEL 2840-20, Makita DCP180 (and 18V dual-18V), DeWalt DCC1054, Bosch GAS 18V-1.",
    ],
    faqs: [
      { q: "How long does a cordless air compressor last on a charge?", a: "Roughly 30–60 brad nails per Ah at 18V — so a 5Ah battery typically drives 150–300 brads or top-ups 4–6 tyres." },
      { q: "Are cordless compressors strong enough for nail guns?", a: "Yes — modern 18V and 36V cordless compressors comfortably run brad and finish nailers. Heavy framing nailers prefer mains air." },
      { q: "Cordless or mains compressor for site work?", a: "Cordless for mobile first-fix and tyre work; mains for any sustained workshop or spray application." },
    ],
    amazonQuery: "cordless air compressor",
  },
  {
    slug: "230v-air-compressors",
    title: "230V Air Compressors",
    shortLabel: "230V",
    group: "power",
    h1: `Best 230V Air Compressors UK ${Y}`,
    metaTitle: `Best 230V Air Compressors UK ${Y} | Single-Phase Reviews`,
    metaDescription:
      "Reviews of 230V single-phase air compressors for UK homes, garages and small workshops. Plug-in models from 6L to 200L.",
    intro:
      "230V single-phase compressors plug into the standard UK domestic supply. They cover everything from a 6L pancake to a 200L vertical workshop unit — basically any compressor you'd find in a home garage or small workshop.",
    longCopy: [
      "Single-phase 230V compressors run up to about 3HP (2.2kW) on a standard 13A plug. Above 3HP you'll usually need a 16A blue-cee plug or a hard-wired connection.",
      "Choose oil-free for portable use, belt-drive for sustained workshop duty. Most UK DIY and trade compressors are 230V — three-phase is reserved for industrial sites.",
      "All major UK brands offer 230V ranges: Hyundai, SIP, Clarke, Sealey, DeWalt, Makita and ABAC.",
    ],
    faqs: [
      { q: "Can I plug a workshop compressor into a normal 13A socket?", a: "Up to about 2.5HP yes. Larger compressors need a 16A commando socket or hard-wired connection to avoid blowing fuses on start-up." },
      { q: "Do 230V compressors need a special circuit?", a: "Domestic 13A ring main is fine for compressors up to 2.5HP. Anything larger benefits from a dedicated 16A circuit." },
      { q: "230V vs 110V on site?", a: "230V at home; 110V for many UK building sites (CDM safety regs). Most site-friendly compressors are dual-voltage." },
    ],
    amazonQuery: "230V air compressor",
  },
  {
    slug: "110v-air-compressors",
    title: "110V Air Compressors",
    shortLabel: "110V",
    group: "power",
    h1: `Best 110V Air Compressors UK ${Y}`,
    metaTitle: `Best 110V Air Compressors UK ${Y} | Site Compressor Reviews`,
    metaDescription:
      "Independent UK reviews of 110V site air compressors. Compliant with CDM site regulations for building, construction and trade use.",
    intro:
      "110V air compressors are the safety-spec voltage on UK building sites — required by most CDM regulations to reduce electric shock risk. They run from site transformers and are typically yellow-bodied for easy identification.",
    longCopy: [
      "Most UK 110V compressors are wheeled portable units from 24L to 100L delivering 5–14 CFM. Brands like SIP, Clarke and Hyundai offer fully 110V-spec site ranges with yellow casings.",
      "Best for: construction sites, listed building work, civils and any commercial setting where site supply is 110V centre-tapped.",
      "Top UK 110V picks: SIP Airmate 110V series, Clarke Airmaster 110V, Hyundai HY7524-110.",
    ],
    faqs: [
      { q: "Do all UK sites need 110V compressors?", a: "Most commercial CDM-regulated sites do. Private domestic jobs and most workshops use 230V." },
      { q: "Can I convert a 230V compressor to 110V?", a: "No — the motor windings are different. You need a purpose-built 110V model or a step-down transformer rated for the start-up current." },
      { q: "Are 110V compressors weaker?", a: "Same CFM and pressure as their 230V cousins, just with different motor windings. Performance is identical." },
    ],
    amazonQuery: "110V air compressor",
  },
  {
    slug: "3-phase-air-compressors",
    title: "3-Phase Air Compressors",
    shortLabel: "3-Phase",
    group: "power",
    h1: `Best 3-Phase Air Compressors UK ${Y}`,
    metaTitle: `Best 3-Phase Air Compressors UK ${Y} | Industrial Compressor Reviews`,
    metaDescription:
      "Independent UK reviews of 3-phase 415V air compressors. Industrial-grade workshop and rotary screw compressors for high-CFM trade use.",
    intro:
      "3-phase (415V) air compressors deliver more power per amp than single-phase units, run cooler and last longer under sustained load. They're the industrial standard above 4kW.",
    longCopy: [
      "Most 3-phase compressors in the UK are 4–22kW belt-drive piston or rotary screw units delivering 18–100+ CFM. Three-phase rotary screws are especially efficient for body shops, fabricators and production workshops.",
      "Best for: multi-bay body shops, fabrication, machine shops, large agricultural workshops, industrial plants.",
      "Top UK 3-phase picks: SIP Airmate ISBD15/270-3, Hyundai HY3300V-3, Atlas Copco GA series, ABAC PRO B6000.",
    ],
    faqs: [
      { q: "Do I need 3-phase for a workshop compressor?", a: "Only above about 4kW (18+ CFM). Single-phase 240V handles most small workshops just fine." },
      { q: "Can I run a 3-phase compressor on single-phase?", a: "Only with a variable-frequency drive (VFD) converter — adds cost and complexity. Better to spec the right voltage from new." },
      { q: "Are 3-phase compressors more efficient?", a: "Yes — they typically run 10–20% more efficiently per CFM and last longer thanks to lower motor current per phase." },
    ],
    amazonQuery: "3 phase air compressor",
  },

  // ============ BY NOISE / ENVIRONMENT ============
  {
    slug: "ultra-quiet-air-compressors",
    title: "Ultra Quiet Air Compressors",
    shortLabel: "Ultra Quiet",
    group: "noise",
    h1: `Best Ultra Quiet Air Compressors UK ${Y}`,
    metaTitle: `Best Ultra Quiet Air Compressors UK ${Y} | Whisper-Quiet Reviews`,
    metaDescription:
      "Independent UK reviews of ultra quiet air compressors at 55–65 dB. Perfect for indoor workshops, studios, offices and shared spaces.",
    intro:
      "Ultra quiet air compressors run at 55–65 dB — roughly the volume of a normal conversation. They're a transformative tool for indoor workshops, studios, dental practices and any environment where noise matters.",
    longCopy: [
      "Achieving sub-65 dB takes brushless or low-RPM induction motors, oil-free pumps and acoustic enclosures. Expect 1.5–3 CFM from most ultra-quiet models — perfect for nailing, airbrushing and short-burst impact work.",
      "Trade-offs include lower CFM than equivalent loud direct-drive models and a higher price tag. For pure indoor use the noise savings are usually worth it.",
      "Top UK ultra quiet picks: Bambi BB24, Hyundai HY27552, California Air Tools 8010, SGS SC25 silent.",
    ],
    faqs: [
      { q: "How quiet is 60 dB?", a: "Roughly the volume of a normal indoor conversation. You can have a phone call near a 60 dB compressor without raising your voice." },
      { q: "Are ultra quiet compressors as powerful as standard ones?", a: "Per-litre tank yes, but lower CFM. Best for inflation, nailing and short bursts rather than continuous spraying or sandblasting." },
      { q: "Are ultra quiet compressors more expensive?", a: "Typically 30–60% more than a standard direct-drive of the same size. Worth it for indoor or shared spaces." },
    ],
    amazonQuery: "ultra quiet air compressor",
  },
  {
    slug: "low-noise-air-compressors",
    title: "Low Noise Air Compressors",
    shortLabel: "Low Noise",
    group: "noise",
    h1: `Best Low Noise Air Compressors UK ${Y}`,
    metaTitle: `Best Low Noise Air Compressors UK ${Y} | Quiet Compressor Reviews`,
    metaDescription:
      "Reviews of low noise air compressors in the UK at 65–80 dB. Quiet enough for home workshops without the price premium of ultra-silent models.",
    intro:
      "Low noise air compressors hit the sweet spot between standard 90 dB direct-drive units and premium ultra-quiet 60 dB silent compressors. They run at 65–80 dB — comfortable for a home garage without needing ear defenders.",
    longCopy: [
      "Most low-noise compressors use oil-free direct-drive pumps with acoustic damping, brushless motors or slightly lower RPMs to bring noise down 10–15 dB versus a standard pancake.",
      "Best for: home workshops, semi-attached garages, mobile valeting setups and any space where standard direct-drive compressors are too disruptive.",
      "Top UK low-noise picks: Hyundai HY7524-50 quiet, Bostitch BTFP02012, Clarke Bandit Plus, SIP Airmate Tornado quiet.",
    ],
    faqs: [
      { q: "Low noise vs silent compressor?", a: "Low noise sits at 65–80 dB; silent models target 55–65 dB. The price difference can be £100–£300." },
      { q: "Are low noise compressors OK for indoor use?", a: "Yes for short bursts. For continuous indoor work in occupied spaces, step up to a true silent (55–65 dB) model." },
      { q: "How loud is 75 dB?", a: "About the noise of a vacuum cleaner or a busy restaurant — louder than conversation but not painful." },
    ],
    amazonQuery: "low noise air compressor",
  },
  {
    slug: "workshop-air-compressors",
    title: "Workshop Air Compressors",
    shortLabel: "Workshop",
    group: "noise",
    h1: `Best Workshop Air Compressors UK ${Y}`,
    metaTitle: `Best Workshop Air Compressors UK ${Y} | Reviews & Buying Guide`,
    metaDescription:
      "Independent UK reviews of workshop air compressors. Compare 50L–270L belt-drive units for serious DIY, trade and small business workshops.",
    intro:
      "‘Workshop’ compressors are the everyday backbone of a working garage or small business shop — typically 50L–270L belt-drive units with 8–20 CFM and a 5,000-hour-plus design life.",
    longCopy: [
      "Choose based on the biggest tool you'll routinely run, with 25% CFM headroom. For a single-bay garage, 100–150L at 12+ CFM covers most jobs. For two-bay or body shop use, step up to 200L+.",
      "Belt-drive is the default — quieter, cooler-running, and longer-lasting than direct-drive at the same size. Two-stage models reach 12+ bar for high-pressure applications.",
      "Top UK workshop picks: SIP Airmate ISBD11/200, Hyundai HY3150V, Clarke SE16C150, Sealey SAC52775B.",
    ],
    faqs: [
      { q: "What size workshop compressor do I need?", a: "Match CFM to your largest tool plus 25%. For most one-person workshops, 100–150L at 12+ CFM is plenty." },
      { q: "Should I buy oil or oil-free for a workshop?", a: "Oil-lubricated belt-drive for sustained use; oil-free for light or intermittent shops." },
      { q: "How long should a workshop compressor last?", a: "A quality belt-drive workshop compressor should comfortably exceed 5,000 hours of trade use with annual oil changes." },
    ],
    amazonQuery: "workshop air compressor",
  },
  {
    slug: "garage-air-compressors",
    title: "Garage Air Compressors",
    shortLabel: "Garage",
    group: "noise",
    h1: `Best Garage Air Compressors UK ${Y}`,
    metaTitle: `Best Garage Air Compressors UK ${Y} | Reviews`,
    metaDescription:
      "Reviews of the best air compressors for garages in the UK. Compare 24L–200L models for impact wrenches, spray painting and tyre fitting.",
    intro:
      "A ‘garage compressor’ is shorthand for the kit that anchors a home or independent garage — typically 50–100L belt-drive units with enough CFM to handle impact wrenches, brad nailing, blowing and the occasional touch-up spray.",
    longCopy: [
      "Match CFM to your most demanding regular tool. For tyre and basic mechanic work, 8–12 CFM at 8 bar covers ½″ impacts. For spray painting, plan on 14–18 CFM at 8 bar.",
      "Quiet models exist for garages attached to homes (55–65 dB silent units). Standard belt-drive workshop units run 80–88 dB.",
      "Top UK garage picks: SIP Airmate ISBD7/100, Hyundai HY30100, Clarke SE16C100, Sealey SAC1103B.",
    ],
    faqs: [
      { q: "What size garage compressor do I need?", a: "For most home garages, a 50–100L belt-drive at 10–14 CFM covers everything from tyre work to occasional spray painting." },
      { q: "Should garage compressors be silent?", a: "Highly recommended if the garage is attached to a house. Silent models add £100–£300 to the price but transform daily use." },
      { q: "Do I need to bolt down a garage compressor?", a: "Vertical 100L+ compressors should be bolted to a concrete floor for stability. Horizontal portable units are usually wheeled and don't require fixing." },
    ],
    amazonQuery: "garage air compressor",
  },
  {
    slug: "industrial-air-compressors",
    title: "Industrial Air Compressors",
    shortLabel: "Industrial",
    group: "noise",
    h1: `Best Industrial Air Compressors UK ${Y}`,
    metaTitle: `Best Industrial Air Compressors UK ${Y} | Heavy-Duty Reviews`,
    metaDescription:
      "Independent UK reviews of industrial air compressors. Compare large belt-drive piston and rotary screw compressors for production, fabrication and continuous duty.",
    intro:
      "Industrial air compressors are designed for 100% duty cycle, multi-tool simultaneous use, and decades of service life. Most are 4–22kW three-phase rotary screw units, with belt-drive piston compressors filling the 200–500L niche.",
    longCopy: [
      "Choose rotary screw for any application running compressed air more than 50% of an 8-hour day. Two-stage belt-drive piston compressors with 500L+ tanks are the alternative for shock-load workshops.",
      "Plan for refrigerated air dryers, oil coalescing filters and a proper ring-main distribution to take full advantage of an industrial-grade compressor.",
      "Top UK industrial picks: Atlas Copco GA series, Hyundai HY100, ABAC PRO B6000, Ingersoll Rand SSR, SIP Airmate Pro.",
    ],
    faqs: [
      { q: "What defines an industrial air compressor?", a: "100% duty cycle, 10,000+ hour design life, three-phase power, and the ability to run multiple high-CFM tools simultaneously." },
      { q: "Rotary screw or piston for industrial use?", a: "Rotary screw for continuous load; piston for intermittent high-pressure work or where capital budget is limited." },
      { q: "Do industrial compressors need special installation?", a: "Yes — three-phase wiring, condensate drain, refrigerated dryer and dedicated ventilation are typical. Many require professional commissioning." },
    ],
    amazonQuery: "industrial air compressor",
  },
  {
    slug: "commercial-air-compressors",
    title: "Commercial Air Compressors",
    shortLabel: "Commercial",
    group: "noise",
    h1: `Best Commercial Air Compressors UK ${Y}`,
    metaTitle: `Best Commercial Air Compressors UK ${Y} | Trade Compressor Reviews`,
    metaDescription:
      "Reviews of commercial air compressors in the UK. Reliable, mid-duty belt-drive and screw compressors for body shops, tyre depots and trade workshops.",
    intro:
      "Commercial air compressors sit between heavy-duty industrial machines and consumer workshop units. They're built for daily trade use — tyre depots, body shops, sign-makers, automotive workshops — where reliability and easy servicing matter.",
    longCopy: [
      "Most commercial compressors are 100–270L belt-drive twin-cylinder units at 12–25 CFM, or compact rotary screw units at 5.5–7.5kW delivering 20–35 CFM continuously.",
      "Look for cast-iron pumps, induction motors, integrated dryers and proper service backup. Annual servicing keeps a commercial compressor running for 10+ years.",
      "Top UK commercial picks: SIP Airmate ISBD11/200, Hyundai HY3300V, ABAC PRO B6000, Clarke SE26C270.",
    ],
    faqs: [
      { q: "Commercial vs industrial compressor?", a: "Commercial: 6–10 year design life, daily trade use. Industrial: 15–25 year design life, continuous production." },
      { q: "Do commercial compressors come with warranties?", a: "Yes — typically 2–5 years on the pump and motor, often with on-site servicing options from UK distributors." },
      { q: "Three-phase for commercial use?", a: "Most commercial compressors above 4kW are three-phase. Single-phase versions exist up to about 3kW." },
    ],
    amazonQuery: "commercial air compressor",
  },
  {
    slug: "home-garage-air-compressors",
    title: "Home Garage Air Compressors",
    shortLabel: "Home Garage",
    group: "noise",
    h1: `Best Home Garage Air Compressors UK ${Y}`,
    metaTitle: `Best Home Garage Air Compressors UK ${Y} | Reviews`,
    metaDescription:
      "Reviews of the best air compressors for home garages in the UK. Quiet, affordable 24L–100L models for hobby mechanics and DIYers.",
    intro:
      "A home garage compressor needs to be quiet, compact and capable of running the most common DIY tools. Most users settle on a 50L belt-drive at 8–10 CFM, or a 24L silent compressor for indoor-friendly operation.",
    longCopy: [
      "For light use, a 24L oil-free direct-drive unit at 5–6 CFM is plenty — covers tyres, nailing, dusters and brief impact work. Step up to a 50L belt-drive for sustained spray and impact use.",
      "Choose a silent compressor if your garage is attached to the house. Standard direct-drive models at 85+ dB are unpleasant indoors and can upset neighbours.",
      "Top UK home garage picks: SIP Airmate Tornado, Hyundai HY24-100, Bambi BB50V silent, Sealey SAC5030E.",
    ],
    faqs: [
      { q: "What size compressor for a home garage?", a: "24L for occasional DIY; 50L for regular work including spray painting; 100L if you'll run multiple tools or sustained spray work." },
      { q: "Are silent compressors worth it for home garages?", a: "Highly recommended for attached garages and shared homes. Silent units are 50% quieter perceived loudness vs standard direct-drive." },
      { q: "How long do home garage compressors last?", a: "Light-use DIY compressors typically last 5–10 years. Trade-grade belt-drive units used at home can comfortably exceed 15 years." },
    ],
    amazonQuery: "home garage air compressor",
  },

  // ============ BY CFM / PSI ============
  {
    slug: "high-cfm-air-compressors",
    title: "High CFM Air Compressors",
    shortLabel: "High CFM",
    group: "spec",
    h1: `Best High CFM Air Compressors UK ${Y}`,
    metaTitle: `Best High CFM Air Compressors UK ${Y} | Reviews`,
    metaDescription:
      "Independent UK reviews of high-CFM air compressors. Compare 15+ CFM compressors for body shops, sandblasting, spray painting and trade workshops.",
    intro:
      "When CFM is your priority — for sandblasting, sustained spray painting or running multiple tools at once — you need a workshop-grade belt-drive piston or rotary screw compressor. Anything below 15 CFM is hobby territory.",
    longCopy: [
      "True high-CFM compressors start at 15 CFM and run to 100+ CFM for rotary screws. Most UK trade users settle at 18–25 CFM from a 200–270L belt-drive twin-cylinder.",
      "Choose a screw compressor for continuous-duty high-CFM applications. Belt-drive twin-cylinder pistons are cheaper but limited to ~70% duty cycle.",
      "Top UK high-CFM picks: SIP Airmate ISBD15/270, Hyundai HY3300V, ABAC PRO B6000, Atlas Copco rotary screws.",
    ],
    faqs: [
      { q: "What counts as high CFM?", a: "Above 15 CFM at 8 bar is generally considered high-CFM in UK workshops. 25+ CFM is industrial territory." },
      { q: "Do I need three-phase for high CFM?", a: "Above 18 CFM most compressors are three-phase. Single-phase high-CFM units exist but cost more for similar output." },
      { q: "Piston or screw for high CFM?", a: "Screw for continuous use; piston for cost-effective intermittent high-CFM work." },
    ],
    amazonQuery: "high cfm air compressor",
  },
  {
    slug: "14cfm-air-compressors",
    title: "14CFM Air Compressors",
    shortLabel: "14 CFM",
    group: "spec",
    h1: `Best 14 CFM Air Compressors UK ${Y}`,
    metaTitle: `Best 14 CFM Air Compressors UK ${Y} | Workshop Reviews`,
    metaDescription:
      "Reviews of 14 CFM air compressors in the UK. Ideal for body shop spray work, sustained impact wrench use and dual-tool workshops.",
    intro:
      "14 CFM at 8 bar is the magic spec for serious body shop and trade workshop air. It powers HVLP spray guns, ¾″ impact wrenches and air sanders without the compressor cycling constantly.",
    longCopy: [
      "Most 14 CFM compressors are 3HP single-phase or 3-phase belt-drive twin-cylinder units on a 150–200L tank. Expect a 12–14 bar maximum pressure from two-stage models.",
      "Best for: car body workshops, sign-makers, sustained sandblasting cabinet work, dual-tool workshops.",
      "Top UK 14 CFM picks: SIP Airmate ISBD11/200, Hyundai HY3150V, Clarke SE16C200V, Sealey SAC52775B.",
    ],
    faqs: [
      { q: "Is 14 CFM enough for a body shop?", a: "Yes for single-bay body shops. For multi-bay or production use, step up to 18–25 CFM." },
      { q: "Single-phase 14 CFM compressor?", a: "Yes — most UK 14 CFM compressors run on single-phase 240V with a 16A commando plug." },
      { q: "Tank size for a 14 CFM pump?", a: "150–200L is the sweet spot. Smaller tanks cycle too often; larger tanks add cost without much benefit at this CFM." },
    ],
    amazonQuery: "14 cfm air compressor",
  },
  {
    slug: "20cfm-air-compressors",
    title: "20CFM Air Compressors",
    shortLabel: "20 CFM",
    group: "spec",
    h1: `Best 20 CFM Air Compressors UK ${Y}`,
    metaTitle: `Best 20 CFM Air Compressors UK ${Y} | Industrial Compressor Reviews`,
    metaDescription:
      "Reviews of 20 CFM air compressors in the UK. Heavy-duty workshop and small body-shop compressors for sustained spray painting and high-volume air tools.",
    intro:
      "20 CFM at 8 bar lands you firmly in industrial territory. Most 20 CFM compressors are 4HP+ three-phase belt-drive piston units on 200–270L tanks, or compact rotary screw compressors.",
    longCopy: [
      "Best for: production body shops, fabrication, sustained sandblasting, sign-makers running multiple tools simultaneously.",
      "Expect 4HP three-phase belt-drive or 5.5kW rotary screw layouts. Two-stage 20 CFM piston pumps reach 12–15 bar for high-pressure applications.",
      "Top UK 20 CFM picks: SIP Airmate ISBD15/270-3, Hyundai HY30300, ABAC PRO B6000, Atlas Copco GA5.",
    ],
    faqs: [
      { q: "Is 20 CFM industrial?", a: "Yes — 20 CFM at 8 bar continuous is firmly in industrial-duty territory and almost always three-phase." },
      { q: "What can I run with 20 CFM?", a: "Sustained HVLP spray painting, full-size sandblasting, ¾″ and 1″ impact wrenches, and multiple smaller air tools simultaneously." },
      { q: "Rotary screw 20 CFM vs belt drive?", a: "Screw is better for any application using compressed air more than 50% of an 8-hour day. Belt drive is cheaper for intermittent use." },
    ],
    amazonQuery: "20 cfm air compressor",
  },
  {
    slug: "145-psi-air-compressors",
    title: "145 PSI Air Compressors",
    shortLabel: "145 PSI",
    group: "spec",
    h1: `Best 145 PSI Air Compressors UK ${Y}`,
    metaTitle: `Best 145 PSI Air Compressors UK ${Y} | 10 Bar Compressor Reviews`,
    metaDescription:
      "Reviews of 145 PSI (10 bar) air compressors in the UK. Standard single-stage workshop compressors for impact wrenches, spray painting and trade use.",
    intro:
      "145 PSI (10 bar) is the standard maximum pressure for single-stage piston compressors. It's plenty for almost every common air tool and gives you headroom to drop pressure at the tool without starving CFM.",
    longCopy: [
      "Most UK single-stage piston compressors top out at 10 bar / 145 PSI. Two-stage models go higher (12–15 bar) for sandblasting and pro-grade impact work.",
      "Best for: every common workshop tool — impact wrenches, brad nailers, spray guns, tyre inflation, dusters and sanders.",
      "Top UK 145 PSI picks: SIP Airmate, Hyundai HY series, Clarke SE series, Sealey SAC series.",
    ],
    faqs: [
      { q: "Is 145 PSI enough for air tools?", a: "Yes — almost all common air tools operate at 60–120 PSI. 145 PSI gives you ample headroom." },
      { q: "Do I ever need more than 145 PSI?", a: "Yes for sandblasting, certain impact pro-grade tools and some industrial nailers. Choose a two-stage compressor at 12–15 bar." },
      { q: "Can a 145 PSI compressor spray cars?", a: "Yes — most HVLP guns operate at 29 PSI atomising pressure, well below the 145 PSI tank pressure." },
    ],
    amazonQuery: "145 psi air compressor",
  },
  {
    slug: "high-pressure-air-compressors",
    title: "High Pressure Air Compressors",
    shortLabel: "High Pressure",
    group: "spec",
    h1: `Best High Pressure Air Compressors UK ${Y}`,
    metaTitle: `Best High Pressure Air Compressors UK ${Y} | 12+ Bar Compressor Reviews`,
    metaDescription:
      "Reviews of high pressure air compressors (12 bar and above) in the UK. Two-stage models for sandblasting, plasma cutting and pro-grade impact work.",
    intro:
      "High pressure air compressors deliver 12 bar and above — well beyond single-stage workshop machines. They're essential for sandblasting, plasma cutting at high amperage, certain pro impact tools and PCP airgun filling.",
    longCopy: [
      "Two-stage piston compressors hit 12–15 bar by compressing air through two cylinders in series. Three-stage and four-stage units are used for breathing air, scuba and PCP airgun filling at 200–300 bar.",
      "Best for: sandblasting cabinet work, plasma cutting, certain impact pro tools, PCP airgun filling (with appropriate filtration).",
      "Top UK high pressure picks: SIP Airmate Two-Stage, Hyundai HY3200V, ABAC PRO B6000 two-stage, Bauer compressors for breathing air.",
    ],
    faqs: [
      { q: "What is high pressure for an air compressor?", a: "Above 12 bar (175 PSI) is considered high pressure. Most consumer compressors top out at 10 bar." },
      { q: "Do I need high pressure for spray painting?", a: "No — most spray guns atomise at 29 PSI. High pressure compressors are for sandblasting, PCP and certain pro impact tools." },
      { q: "How is high pressure achieved?", a: "Two-stage piston pumps compress air through two cylinders in series, each contributing roughly half the total pressure rise." },
    ],
    amazonQuery: "high pressure air compressor",
  },

  // ============ BY BRAND ============
  {
    slug: "hyundai-air-compressors",
    title: "Hyundai Air Compressors",
    shortLabel: "Hyundai",
    group: "brand",
    h1: `Best Hyundai Air Compressors UK ${Y}`,
    metaTitle: `Best Hyundai Air Compressors UK ${Y} | Range Reviews`,
    metaDescription:
      "Independent UK reviews of Hyundai air compressors. Compare silent, oil-free, belt-drive and petrol Hyundai compressors from 6L to 270L.",
    intro:
      "Hyundai Power Equipment has built one of the broadest, best-value compressor ranges in the UK — from silent 8L home models up to 270L workshop machines, plus a strong petrol mobile range.",
    longCopy: [
      "Hyundai compressors offer well-specified pumps, decent build quality and a 3-year warranty on most models at sharp price points. The silent range (HY27552, HY7508 silent) competes directly with Bambi and California Air Tools.",
      "Highlights: HY27552 silent (24L), HY3150V (150L workshop), HY30100 (100L), and the HY3050V-petrol mobile.",
      "Hyundai's UK distributor network includes excellent spare parts availability and service support.",
    ],
    faqs: [
      { q: "Are Hyundai air compressors good?", a: "Yes — Hyundai offers strong value across DIY, trade and silent ranges. The 3-year warranty is best-in-class at most price points." },
      { q: "Where are Hyundai compressors made?", a: "Hyundai Power Equipment compressors are designed in the UK and Korea, manufactured across multiple Asian factories under Hyundai licensing." },
      { q: "Hyundai vs Clarke compressor?", a: "Hyundai tends to lead on silent and petrol mobile ranges; Clarke is stronger on workshop belt-drive value." },
    ],
    amazonQuery: "Hyundai air compressor",
  },
  {
    slug: "clarke-air-compressors",
    title: "Clarke Air Compressors",
    shortLabel: "Clarke",
    group: "brand",
    h1: `Best Clarke Air Compressors UK ${Y}`,
    metaTitle: `Best Clarke Air Compressors UK ${Y} | Machine Mart Range Reviews`,
    metaDescription:
      "Independent UK reviews of Clarke (Machine Mart) air compressors. Compare Bandit, Airmaster, SE-series and Tiger ranges from 6L to 270L.",
    intro:
      "Clarke is the in-house brand of Machine Mart and one of the most widely-stocked compressor ranges in the UK. The Bandit, Airmaster, SE-series and Tiger lines cover DIY, trade and workshop applications.",
    longCopy: [
      "Clarke compressors offer good value, easy parts availability through Machine Mart, and a strong trade focus. The SE-series belt-drive workshop range is particularly popular with independent garages.",
      "Highlights: Bandit Plus (24L portable), SE16C150 (150L workshop), SE26C270 (270L three-phase) and the Airmaster site range.",
      "Most Clarke compressors carry a 2-year warranty and have excellent in-store demo and service support at Machine Mart branches.",
    ],
    faqs: [
      { q: "Are Clarke air compressors any good?", a: "Yes — they're solid mid-range workshop and DIY units with excellent UK parts availability through Machine Mart." },
      { q: "Where to buy Clarke compressors?", a: "Direct from Machine Mart stores nationwide, online via machinemart.co.uk, and through select third-party dealers." },
      { q: "Clarke vs SIP compressor?", a: "Clarke is more budget-friendly and easier to source. SIP generally offers more premium pumps at slightly higher prices." },
    ],
    amazonQuery: "Clarke air compressor",
  },
  {
    slug: "sip-air-compressors",
    title: "SIP Air Compressors",
    shortLabel: "SIP",
    group: "brand",
    h1: `Best SIP Air Compressors UK ${Y}`,
    metaTitle: `Best SIP Air Compressors UK ${Y} | Airmate Range Reviews`,
    metaDescription:
      "Independent UK reviews of SIP Industrial air compressors. Compare Airmate, ISBD and Tornado ranges from 6L portable to 500L industrial.",
    intro:
      "SIP Industrial Products has been making air compressors in the UK for decades. The Airmate range covers everything from 6L oil-free units to 500L three-phase workshop machines, all with solid build quality and good UK service backup.",
    longCopy: [
      "SIP's ISBD belt-drive workshop range is particularly highly-regarded by trade users — cast-iron twin-cylinder pumps, induction motors and 5-year warranties on top-of-the-range units.",
      "Highlights: Airmate Tornado 7/24 (24L portable), ISBD11/200 (200L belt-drive), ISBD15/270 (270L three-phase) and Airmate ProDV (petrol mobile).",
      "SIP's UK service network and parts availability is excellent. Most models can be serviced at any SIP authorised dealer.",
    ],
    faqs: [
      { q: "Are SIP air compressors good?", a: "Yes — SIP offers premium build quality at fair prices, with one of the best UK parts and service networks in the industry." },
      { q: "Where are SIP compressors made?", a: "SIP compressors are designed and assembled in the UK with components sourced globally. Some larger industrial units are partially manufactured in Italy." },
      { q: "SIP vs Clarke compressor?", a: "SIP tends to use higher-quality pumps and motors at slightly higher price points. Clarke offers more aggressive entry-level pricing." },
    ],
    amazonQuery: "SIP air compressor",
  },
  {
    slug: "sealey-air-compressors",
    title: "Sealey Air Compressors",
    shortLabel: "Sealey",
    group: "brand",
    h1: `Best Sealey Air Compressors UK ${Y}`,
    metaTitle: `Best Sealey Air Compressors UK ${Y} | Trade Range Reviews`,
    metaDescription:
      "Independent UK reviews of Sealey air compressors. Compare SAC series belt-drive and direct-drive models for trade and serious DIY use.",
    intro:
      "Sealey is a UK trade tools brand stocked through every motor factor and tool retailer in the country. Their SAC compressor range covers DIY 24L portables up to 270L three-phase workshop units.",
    longCopy: [
      "Sealey compressors prioritise easy parts availability and trade-friendly pricing. Most are belt-drive piston units with cast-iron pumps and induction motors.",
      "Highlights: SAC2403E (24L), SAC1153B (150L belt drive), SAC52775B (200L two-stage) and the SAC4060HE petrol mobile.",
      "Sealey compressors come with a 1-year warranty (extendable to 2 years on registration) and excellent UK parts availability through motor factors.",
    ],
    faqs: [
      { q: "Are Sealey air compressors good?", a: "Yes — solid mid-range trade compressors with excellent UK parts and service availability through motor factors." },
      { q: "Sealey vs Clarke compressor?", a: "Very similar quality tier. Sealey leans slightly more towards trade/motor factor customers; Clarke towards Machine Mart's DIY/trade hybrid market." },
      { q: "Where to buy Sealey compressors?", a: "Through any UK motor factor (GSF, Euro Car Parts), tool retailers (Toolstation, Machine Mart) and online direct from Sealey." },
    ],
    amazonQuery: "Sealey air compressor",
  },
  {
    slug: "dewalt-air-compressors",
    title: "DeWalt Air Compressors",
    shortLabel: "DeWalt",
    group: "brand",
    h1: `Best DeWalt Air Compressors UK ${Y}`,
    metaTitle: `Best DeWalt Air Compressors UK ${Y} | Reviews & Buying Guide`,
    metaDescription:
      "Independent UK reviews of DeWalt air compressors. Compare DPC mains and 18V/54V XR cordless compressors for trade and first-fix work.",
    intro:
      "DeWalt's compressor range is small but well-targeted at trade first-fix carpenters and mobile professionals. The DPC mains pancake range and the 18V XR cordless compressors integrate seamlessly with the DeWalt tool ecosystem.",
    longCopy: [
      "DeWalt focuses on portability and integration. The DPC6MRC is a benchmark 6L pancake unit; the DCC1054 18V XR cordless compressor lets you run brad nailers without mains.",
      "Highlights: DPC6MRC (6L mains), DCC1054 (18V XR cordless), DXCMV5048055 (larger workshop).",
      "DeWalt compressors carry a 3-year warranty when registered and benefit from the brand's UK-wide service centre network.",
    ],
    faqs: [
      { q: "Are DeWalt air compressors any good?", a: "Yes for trade carpenters and mobile users. DeWalt focuses on portable and cordless units rather than full workshop compressors." },
      { q: "How long does the DeWalt 18V cordless compressor last on a charge?", a: "Roughly 1200 brad nails per 5Ah battery — comfortably a full day of first-fix work." },
      { q: "DeWalt vs Makita compressor?", a: "DeWalt has a stronger mains pancake range; Makita leads on dual-18V cordless compressors with higher CFM." },
    ],
    amazonQuery: "DeWalt air compressor",
  },
  {
    slug: "makita-air-compressors",
    title: "Makita Air Compressors",
    shortLabel: "Makita",
    group: "brand",
    h1: `Best Makita Air Compressors UK ${Y}`,
    metaTitle: `Best Makita Air Compressors UK ${Y} | Reviews`,
    metaDescription:
      "Independent UK reviews of Makita air compressors. Compare MAC mains models and 18V/twin-18V/40V cordless compressors for trade and site use.",
    intro:
      "Makita's compressor range balances mains-powered pancake units with class-leading cordless compressors. The MAC2400 is a long-standing 24L portable favourite; the 40Vmax XGT and twin-18V LXT cordless compressors are the highest-CFM cordless options in the UK.",
    longCopy: [
      "Makita compressors are oil-lubricated cast-iron pumps with strong reliability records — the MAC2400 has been in production for over 20 years.",
      "Highlights: MAC2400 (24L mains), DCP180 (18V cordless), AC001GZ (40Vmax XGT), AC640 (twin-18V).",
      "Makita's UK distribution and 3-year warranty (registered) makes it a top-tier choice for trade users.",
    ],
    faqs: [
      { q: "Are Makita air compressors any good?", a: "Yes — Makita offers some of the most reliable mains and cordless compressors in the UK, with excellent trade reputation." },
      { q: "Makita 40V vs 18V cordless compressor?", a: "40Vmax XGT delivers higher CFM and longer runtime; twin-18V LXT integrates with the larger 18V tool platform many tradespeople already own." },
      { q: "Where are Makita compressors made?", a: "Most Makita compressors are designed in Japan and manufactured across Japan, China and the UK." },
    ],
    amazonQuery: "Makita air compressor",
  },
  {
    slug: "milwaukee-air-compressors",
    title: "Milwaukee Air Compressors",
    shortLabel: "Milwaukee",
    group: "brand",
    h1: `Best Milwaukee Air Compressors UK ${Y}`,
    metaTitle: `Best Milwaukee Air Compressors UK ${Y} | M18 Cordless Reviews`,
    metaDescription:
      "Independent UK reviews of Milwaukee M18 cordless air compressors. Compare the M18 FUEL 2840-20 and tyre inflator ranges.",
    intro:
      "Milwaukee's compressor range is focused on cordless trade users. The M18 FUEL 2840-20 is the highest-CFM cordless compressor in the UK trade market, designed specifically for site carpenters running brad and finish nailers without mains supply.",
    longCopy: [
      "The 2840-20 delivers 1.2 CFM at 90 PSI from a 2-gallon (7.5L) tank — enough for ~1200 brad nails per HD9.0 battery. The M12 inflator covers tyre and inflation duties.",
      "Highlights: M18 FUEL 2840-20 cordless compressor, M12 2475-20 inflator, M18 2848-20 inflator.",
      "Milwaukee's red livery and HD battery platform make it a popular choice for first-fix carpenters already invested in M18 tools.",
    ],
    faqs: [
      { q: "Is the Milwaukee M18 compressor worth it?", a: "Yes for tradespeople already in the M18 ecosystem who do regular first-fix work. As a standalone purchase the value is less clear." },
      { q: "Can the Milwaukee 2840 run a finish nailer?", a: "Yes — easily handles 16- and 15-gauge finish nailers, brad and pin nailers." },
      { q: "Milwaukee vs DeWalt cordless compressor?", a: "Milwaukee 2840 delivers slightly higher CFM; DeWalt DCC1054 is smaller and lighter. Choose by tool ecosystem." },
    ],
    amazonQuery: "Milwaukee air compressor",
  },
];

// Auto-derive tag keywords (lower-cased) for each subcategory.
export const airCompressorSubcategories: Record<string, AirCompressorSubcategoryConfig> =
  Object.fromEntries(
    seeds.map(seed => {
      const baseTags = Array.from(
        new Set(
          [
            seed.title,
            seed.title.toLowerCase(),
            seed.shortLabel,
            seed.shortLabel.toLowerCase(),
            seed.slug,
            seed.slug.replace(/-/g, " "),
            "air compressor",
            "air compressors",
            seed.amazonQuery,
          ]
            .filter(Boolean)
            .map(s => s.toLowerCase())
        )
      );
      return [seed.slug, { ...seed, tagKeywords: seed.tagKeywords ?? baseTags }];
    })
  );

export const airCompressorSubcategoryList = Object.values(airCompressorSubcategories);

export const airCompressorGroupLabels: Record<AirCompressorGroup, string> = {
  type: "By Type",
  use: "By Use Case",
  tank: "By Tank Size",
  power: "By Power Source",
  noise: "By Environment",
  spec: "By CFM / PSI",
  brand: "By Brand",
};

export const airCompressorGroupOrder: AirCompressorGroup[] = [
  "type",
  "use",
  "tank",
  "power",
  "noise",
  "spec",
  "brand",
];
